import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Copy, History,
  Info, LayoutTemplate, Send, ShieldAlert, Trash2, X, Users, Briefcase,
  CalendarRange, Calendar, Columns3, Repeat, UserMinus, Settings2, SlidersHorizontal,
  Plus, CopyPlus, Sparkles, MapPin, Clock, Coffee, StickyNote, Layers, Eye, ArrowLeft, Check,
  CalendarOff, Hourglass, ExternalLink, Ban, Paperclip, FileText, CalendarClock,
  ClipboardPaste, CalendarPlus, Disc3, UserRound,
} from 'lucide-react'
import { paidMinutesFromSpan } from '@gestion/shared/duration'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Link, usePath } from '../router.js'
import { apiGet, adminMutate, Modal, usePermissions, useToast } from '../admin/primitives.js'
import { Combobox, SegmentedControl, DatePicker, type ComboItem } from '../admin/pickers.js'
import { Field, TextInput, Switch, Checkbox, NumberInput } from '../admin/form.js'
import { Floating } from '../shell/floating.js'
import { useConfirm } from '../shell/confirm.js'
import { AUDIT_ACTIONS, PERMISSION_META } from '../admin/catalog.js'
import { humanName } from './format.js'
import {
  shiftTone, dayAxis, barGeometry, displayRollover, fmtDuration, initials,
  leaveTone, leaveDayLabel, leaveDayGeometry, leaveAbbr, leaveBody, minutesToHm,
  DENSITIES, DENSITY_LABELS, type Density, type PositionTone, type LeaveDraft,
} from './planning-model.js'
import { CHEMIN_PLANNING, lirePersonneParam, bandeauPersonne } from './planning-personne.js'
import { remplacerRecherche } from '../finance/adresse-ecran.js'
import { usePrefsStore } from '../prefs.js'
import './rh.css'
import './planning.css'

/**
 * SIRH LOT 3 → 4.7 — PLANNING : TABLEAU OPÉRATEUR PREMIUM.
 *
 * Skello-grade fonctionnellement, Live Control premium visuellement :
 *   · le POSTE est la couleur métier principale (carte teintée + accent) ;
 *     sans poste → neutre « Poste non renseigné » ;
 *   · toolbar à 2 niveaux, filtres combobox + chips, panneau Affichage
 *     (densité confortable / compacte / ultra, couches affichées) ;
 *   · vues Jour (amplitude configurable, élargie si un shift déborde) /
 *     Semaine / Mois × Employés / Postes (couverture : shifts, à pourvoir) ;
 *   · SHIFT INSPECTOR intégré au layout (pas un drawer) ; composer rapide ;
 *   · drag : déplacement direct, Alt = copie, choix ancré sur touch ; aperçu
 *     de dépôt DANS la cellule cible ; retours dans le toast global ;
 *   · sélection multiple → Bulk Action Bar canonique ;
 *   · versions : brouillon / dernière publication / comparer ;
 *   · ABSENCES (4.7) : composer unique Shift | Absence ouvert depuis la
 *     cellule (double-clic, « + », Entrée), cartes d'absence hachurées
 *     teintées par le TYPE (jamais un poste, jamais draggables), demandes en
 *     attente distinctes, inspector d'absence dans le même panneau.
 */

interface Verdict { code: string; rule: string; severity: 'INFO' | 'WARNING' | 'BLOCK'; message: string; source: string; article: string | null; person_id: string; shift_ids: string[] }
interface Shift {
  id: string; person_id: string | null; starts_at: string; ends_at: string
  break_minutes: number; break_paid: boolean; paid_minutes: number
  position_id: string | null; position_name: string | null; position_color: string | null
  function_id: string | null; function_name: string | null
  location_id: string | null; location_code: string | null
  notes: string | null; source: string; recurrence_group_id: string | null
  /** Équipe cible d'un besoin non assigné (4.7) — absent tant que l'API ne l'expose pas. */
  team_id?: string | null | undefined
  /** Soirée rattachée — toujours choisie explicitement, jamais déduite d'une date. */
  event_id?: string | null | undefined
  event_name?: string | null | undefined
  /** Renseigné quand le service est né d'un pointage (source UNPLANNED). */
  created_from_clock_event_id?: string | null | undefined
}
interface PersonMeta { person_id: string; display_name: string; position: string | null; teams: string[]; state: string | null; weekly_minutes: number | null; employment_kind: string | null }
interface Catalog { positions: { id: string; name: string; color: string | null }[]; functions: { id: string; name: string }[]; locations: { id: string; code: string; name: string }[] }
interface WeekData extends Catalog {
  week_start: string
  shifts: Shift[]
  verdicts: Verdict[]
  people: PersonMeta[]
  publication: { version: number; published_at: string; pending_changes: number } | null
  templates: { id: string; name: string; shifts: number }[]
  policy: { default_break_paid: boolean; display_day_start: number; display_day_end: number }
  /**
   * Périmètre de l'acteur (4.7) : TEAM → un besoin non assigné exige une équipe cible quand plusieurs sont gérées.
   * `can_publish` (4.8) est tranché par le SERVEUR : détenir `time.schedule.publish` ne suffit pas,
   * publier est un acte d'établissement qui exige une autorité GLOBALE. Le web ne le devine pas.
   */
  scope?: { kind: 'ALL' | 'TEAM' | 'SELF' | 'NONE'; teams: { id: string; name: string }[]; can_publish?: boolean | undefined } | undefined
  /** Soirées de la semaine (4.8), par nuit commerciale — VIDE sans `soiree.read` : on n'affiche alors aucun titre. */
  soirees?: Soiree[] | undefined
}
/** Soirée telle que servie par /planning/week — forme garantie par readSoirees. */
interface Soiree { id: string; name: string; night: string; status: 'SCHEDULED' | 'CANCELLED'; starts_at: string }
/** Absence (approuvée OU en attente) telle que servie par /planning/context — forme garantie par normalizeLeave. */
interface LeaveRec {
  id: string; person_id: string; starts_on: string; ends_on: string
  start_half: boolean; end_half: boolean; hours_from: number | null; hours_to: number | null
  status: 'APPROVED' | 'REQUESTED'; type_key: string; type_label: string; color: string | null
  source: string; comment_visible: boolean
  /** Identifiant absent côté serveur (transition) : la carte s'affiche, le détail ne peut pas être chargé. */
  synthetic: boolean
}
interface LeaveTypeRec { id: string; key: string; label: string; color: string | null; document_policy: 'NONE' | 'OPTIONAL' | 'REQUIRED'; allow_hours: boolean; counts_on: string | null }
interface LeaveConflict { shift_id: string; starts_at: string; ends_at: string; position_name: string | null; location_code: string | null }
interface PlanningContext { leaves: LeaveRec[]; availability: Record<string, Record<string, { status: string; from_minutes: number | null; to_minutes: number | null }>> }

/* Garde de forme : pendant la transition d'API certains champs manquent — on
   n'affiche jamais un objet incomplet, on remplit des valeurs sûres. */
function normalizeLeave(raw: unknown): LeaveRec | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const person_id = typeof r['person_id'] === 'string' ? r['person_id'] : null
  const starts_on = typeof r['starts_on'] === 'string' ? r['starts_on'].slice(0, 10) : null
  const ends_on = typeof r['ends_on'] === 'string' ? r['ends_on'].slice(0, 10) : starts_on
  if (!person_id || !starts_on || !ends_on) return null
  const type_key = typeof r['type_key'] === 'string' ? r['type_key'] : 'ABSENCE'
  const hasId = typeof r['id'] === 'string' && r['id'].length > 0
  return {
    id: hasId ? r['id'] as string : `${person_id}:${starts_on}:${type_key}`,
    person_id, starts_on, ends_on,
    start_half: r['start_half'] === true, end_half: r['end_half'] === true,
    hours_from: typeof r['hours_from'] === 'number' ? r['hours_from'] : null,
    hours_to: typeof r['hours_to'] === 'number' ? r['hours_to'] : null,
    status: r['status'] === 'REQUESTED' ? 'REQUESTED' : 'APPROVED',
    type_key, type_label: typeof r['type_label'] === 'string' ? r['type_label'] : 'Absence',
    color: typeof r['color'] === 'string' ? r['color'] : null,
    source: typeof r['source'] === 'string' ? r['source'] : 'SELF',
    comment_visible: r['comment_visible'] === true,
    synthetic: !hasId,
  }
}
/**
 * Garde de forme des soirées (4.8) : `apiGet` RÉSOUT aussi les 403/404/500
 * avec `{ error }`, et un serveur qui n'expose pas encore le champ renvoie
 * simplement rien. Une entrée sans identifiant ou sans nom n'est jamais
 * affichée : on ne met pas de titre vide sous une colonne de planning.
 */
function readSoirees(raw: unknown): Soiree[] {
  if (!Array.isArray(raw)) return []
  const out: Soiree[] = []
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue
    const o = s as Record<string, unknown>
    const id = typeof o['id'] === 'string' ? o['id'] : null
    const name = typeof o['name'] === 'string' ? o['name'].trim() : ''
    const night = typeof o['night'] === 'string' ? o['night'].slice(0, 10) : null
    if (!id || !name || !night) continue
    out.push({
      id, name, night,
      status: o['status'] === 'CANCELLED' ? 'CANCELLED' : 'SCHEDULED',
      starts_at: typeof o['starts_at'] === 'string' ? o['starts_at'] : '',
    })
  }
  return out
}
function readContext(raw: unknown): PlanningContext | null {
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as { leaves?: unknown }).leaves)) return null
  const r = raw as { leaves: unknown[]; availability?: unknown }
  const availability = r.availability && typeof r.availability === 'object' ? r.availability as PlanningContext['availability'] : {}
  return { leaves: r.leaves.map(normalizeLeave).filter((l): l is LeaveRec => l !== null), availability }
}
function readLeaveTypes(raw: unknown): LeaveTypeRec[] | null {
  const arr = Array.isArray(raw) ? raw : raw && typeof raw === 'object' && Array.isArray((raw as { types?: unknown }).types) ? (raw as { types: unknown[] }).types : null
  if (!arr) return null
  const out: LeaveTypeRec[] = []
  for (const t of arr) {
    if (!t || typeof t !== 'object') continue
    const o = t as Record<string, unknown>
    if (typeof o['id'] !== 'string' || typeof o['label'] !== 'string') continue
    const pol = o['document_policy']
    out.push({
      id: o['id'], key: typeof o['key'] === 'string' ? o['key'] : '', label: o['label'],
      color: typeof o['color'] === 'string' ? o['color'] : null,
      document_policy: pol === 'REQUIRED' || pol === 'OPTIONAL' ? pol : o['requires_document'] === true ? 'REQUIRED' : 'NONE',
      allow_hours: o['allow_hours'] !== false,
      counts_on: typeof o['counts_on'] === 'string' ? o['counts_on'] : null,
    })
  }
  return out
}
function fmtDateLong(ymd: string): string { return new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }
function fmtDateShort(ymd: string): string { return new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) }
interface RangeData extends Pick<Catalog, 'positions'> { from_week: string; to_week: string; shifts: Shift[]; people: PersonMeta[] }
interface DiffEntry { kind: 'ADDED' | 'REMOVED' | 'CHANGED'; person_id: string | null; shift_id: string; before: SnapRow | null; after: SnapRow | null }
interface SnapRow { id: string; person_id: string | null; starts_at: string; ends_at: string; break_minutes: number; position_id: string | null; location_id: string | null }
interface PublishPreview { version_next: number; diff: DiffEntry[]; persons: { person_id: string; display_name: string; has_account: boolean; changes: number }[]; unassigned_changes: number; blocks: Verdict[] }
interface DiffData { week: string; diff: DiffEntry[]; persons: { person_id: string; display_name: string; has_account: boolean; changes: number }[]; unassigned_changes: number; last_publication: { version: number; published_at: string; published_by: string } | null; pending_changes: number }

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const PARIS = 'Europe/Paris'
const UNASSIGNED = '__unassigned__'

function addDays(ymd: string, n: number): string { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
/** Écart en jours entre deux dates civiles (midi UTC : aucun piège d'heure d'été). */
function daysBetween(from: string, to: string): number { return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000) }
function mondayOfYmd(ymd: string): string { const noon = new Date(`${ymd}T12:00:00Z`); noon.setUTCDate(noon.getUTCDate() - ((noon.getUTCDay() + 6) % 7)); return noon.toISOString().slice(0, 10) }
function parisYmd(iso: string): string { return new Intl.DateTimeFormat('fr-CA', { timeZone: PARIS, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso)) }
function parisHm(iso: string): string { return new Intl.DateTimeFormat('fr-FR', { timeZone: PARIS, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) }
function parisHourFloat(iso: string): number { const hm = parisHm(iso); return Number(hm.slice(0, 2)) + Number(hm.slice(3)) / 60 }
function dayIndex(week: string, iso: string): number { const d = parisYmd(iso); for (let i = 0; i < 7; i++) if (addDays(week, i) === d) return i; return -1 }
function fmtH(min: number): string { const hh = Math.floor(Math.abs(min) / 60); const mm = Math.abs(min) % 60; const s = min < 0 ? '−' : ''; return mm ? `${s}${hh}h${String(mm).padStart(2, '0')}` : `${s}${hh}h` }
function shiftInstantByDays(iso: string, days: number): string { return parisLocalToIso(addDays(parisYmd(iso), days), parisHm(iso)) }
function parisLocalToIso(ymd: string, hm: string): string {
  for (const off of ['+02:00', '+01:00']) { const d = new Date(`${ymd}T${hm}:00${off}`); if (parisYmd(d.toISOString()) === ymd && parisHm(d.toISOString()) === hm) return d.toISOString() }
  return new Date(`${ymd}T${hm}:00+01:00`).toISOString()
}
function addMinutesIso(iso: string, minutes: number): string { return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString() }
function todayYmd(): string { return new Intl.DateTimeFormat('fr-CA', { timeZone: PARIS, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()) }
function floatingAnchor(x: number, y: number): HTMLElement {
  return { getBoundingClientRect: () => ({ top: y, bottom: y, left: x, right: x, width: 0, height: 0, x, y, toJSON: () => ({}) }) } as unknown as HTMLElement
}
function isoWeekNumber(ymd: string): number {
  const d = new Date(`${ymd}T12:00:00Z`); const day = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - day + 3)
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4))
  return 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86_400_000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7)
}

type UndoAction = { label: string; run: () => Promise<void> }
type View = 'day' | 'week' | 'month'
type Mode = 'employees' | 'positions'
/**
 * MENU CONTEXTUEL (4.8) — UN SEUL état, discriminé par la cible.
 *
 *   · `shift`    : une carte de shift (semaine) ou une barre (vue Jour) ;
 *   · `cell`     : le fond d'une cellule (personne, jour) — vide OU occupée ;
 *                  `unassigned` = la ligne « Non assignés », qui n'appartient
 *                  à personne : elle n'accepte donc jamais d'absence ;
 *   · `position` : vue Postes, la cellule est (poste, jour) sans salarié —
 *                  pas d'absence non plus, une absence appartient à quelqu'un.
 *
 * Il n'existe pas de second état de menu : deux états, c'était deux menus
 * ouverts en même temps.
 */
type CtxTarget =
  | { kind: 'shift'; shift: Shift }
  | { kind: 'cell'; personId: string | null; day: string; unassigned: boolean }
  | { kind: 'position'; positionId: string | null; day: string }
type Ctx = { x: number; y: number; target: CtxTarget }
interface Layers { unassigned: boolean; leaves: boolean; availability: boolean; totals: boolean; alerts: boolean }
type EditorState = {
  shift?: Shift; personId: string | null; day: string; tab?: 'shift' | 'leave' | undefined
  /** Poste de la LIGNE cliquée en vue Postes : le menu le nomme, le formulaire doit le poser. */
  positionId?: string | null | undefined
}
/** Ce que l'inspector montre : un shift OU une absence — jamais les deux (état discriminé, param URL shift= / leave=). */
type Inspect = { kind: 'shift' | 'leave'; id: string }

/** Clavier dans la grille : flèches entre cellules (même ligne ← →, même jour ↑ ↓). */
function moveCellFocus(e: React.KeyboardEvent<HTMLElement>): void {
  const el = e.currentTarget
  const grid = el.closest('.pl-grid'); if (!grid) return
  const day = Number(el.dataset['day'])
  let target: HTMLElement | null = null
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    const attr = el.dataset['person'] !== undefined ? 'person' : 'row'
    const key = el.dataset[attr] ?? ''
    target = grid.querySelector<HTMLElement>(`[data-cell][data-${attr}="${CSS.escape(key)}"][data-day="${day + (e.key === 'ArrowRight' ? 1 : -1)}"]`)
  } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    const col = [...grid.querySelectorAll<HTMLElement>(`[data-cell][data-day="${day}"]`)]
    const i = col.indexOf(el); if (i < 0) return
    target = col[i + (e.key === 'ArrowDown' ? 1 : -1)] ?? null
  } else return
  if (target) { e.preventDefault(); target.focus() }
}

/**
 * Clavier d'une cellule : Entrée ou Espace ouvre le composer, flèches → cellule
 * voisine. Une touche venant d'un enfant (chip, carte d'absence, bouton « + »)
 * n'est pas interceptée — l'enfant a son propre comportement.
 */
function cellKeyDown(e: React.KeyboardEvent<HTMLElement>, open: () => void): void {
  if (e.target !== e.currentTarget) return
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); return }
  moveCellFocus(e)
}

/**
 * Pointeur sur une cellule : c'est la CELLULE qui prend le focus (recette 4.7).
 * Sans cela, le clic sur une cellule vide tombait sur le bouton « + » invisible
 * (position absolue, il couvre toute la cellule) : ce bouton `tabIndex=-1`
 * prenait le focus, ouvrait le composer, et la modale lui rendait le focus à la
 * fermeture — flèches mortes (le gestionnaire clavier vit sur la cellule), aucun
 * anneau visible — ou le rendait à un nœud démonté (→ BODY) dès que la cellule
 * s'était remplie (shift ou absence créé). Ici le « + » ne vole plus le focus
 * (son clic ouvre toujours le composer) ; les enfants interactifs (chip, carte
 * d'absence, lien) gardent le leur.
 */
function focusCellFromPointer(e: React.MouseEvent<HTMLElement>): void {
  const t = e.target as HTMLElement
  if (t.closest('.pl-chip, .pl-leave, a, input')) return
  if (t.closest('.pl-add')) e.preventDefault()
  e.currentTarget.focus()
}

/** « vendredi 4 septembre 2026 » → « Vendredi 4 septembre 2026 » : la première lettre seulement, jamais `text-transform: capitalize`. */
function capFirst(s: string): string { return s ? s.charAt(0).toLocaleUpperCase('fr-FR') + s.slice(1) : s }

export function PlanningPage() {
  // Préférence « Menu clic droit » coupée : le menu du navigateur revient ici aussi.
  const menuGestion = usePrefsStore().resolved['context_menu'] !== 'off'
  const qs = new URLSearchParams(window.location.search)
  const [view, setView] = useState<View>((qs.get('view') as View) || 'week')
  const [mode, setMode] = useState<Mode>((qs.get('mode') as Mode) || 'employees')
  const [density, setDensity] = useState<Density>(() => {
    const u = qs.get('density'); if (u && (DENSITIES as readonly string[]).includes(u)) return u as Density
    try { const s = localStorage.getItem('gestion.planning.density'); if (s && (DENSITIES as readonly string[]).includes(s)) return s as Density } catch { /* privé */ }
    return 'comfortable'
  })
  const [refDay, setRefDay] = useState(qs.get('date') || todayYmd())
  const week = mondayOfYmd(refDay)
  const [teamFilter, setTeamFilter] = useState(qs.get('team') ?? '')
  const [positionFilter, setPositionFilter] = useState(qs.get('position') ?? '')
  const [roomFilter, setRoomFilter] = useState(qs.get('room') ?? '')
  const [query, setQuery] = useState(qs.get('q') ?? '')
  // ?personne= : filtre EXACT sur person.id ; ?q= reste une recherche sur le nom. Lu au montage, comme les autres paramètres.
  // App.tsx remonte la page à chaque navigation (key={navigations}) : un favori, la palette (Ctrl+K), la tuile du hub Temps
  // ou un Retour relisent l'adresse, même déjà sur le planning. Les réécritures de la page (remplacerRecherche) ne la remontent pas.
  const [personFilter, setPersonFilter] = useState(() => lirePersonneParam(qs.get('personne')))
  const [layers, setLayers] = useState<Layers>(() => {
    const def: Layers = { unassigned: true, leaves: true, availability: true, totals: true, alerts: true }
    try { const s = localStorage.getItem('gestion.planning.layers'); if (s) Object.assign(def, JSON.parse(s) as Partial<Layers>) } catch { /* privé */ }
    if (qs.get('hideUnassigned') === '1') def.unassigned = false
    return def
  })
  const [inspect, setInspect] = useState<Inspect | null>(() => {
    const s = qs.get('shift'); if (s) return { kind: 'shift', id: s }
    const l = qs.get('leave'); if (l) return { kind: 'leave', id: l }
    return null
  })
  // Le contexte (absences + disponibilités) se recharge avec la semaine ET après chaque mutation (load).
  const [ctxTick, setCtxTick] = useState(0)

  const [d, setD] = useState<WeekData | null>(null)
  const [range, setRange] = useState<RangeData | null>(null)
  const [diff, setDiff] = useState<DiffData | null>(null)
  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [clipboard, setClipboard] = useState<Shift[]>([])
  const [verdictPanel, setVerdictPanel] = useState(false)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [repeatFor, setRepeatFor] = useState<Shift | null>(null)
  const [publish, setPublish] = useState<PublishPreview | null>(null)
  const [versionsOpen, setVersionsOpen] = useState(false)
  const [tplOpen, setTplOpen] = useState(false)
  const [clearOpen, setClearOpen] = useState(false)
  const [ctx, setCtx] = useState<Ctx | null>(null)
  const path = usePath()
  const perms = usePermissions()
  const toast = useToast()
  const canEdit = !perms.loading && perms.permissions.includes('time.schedule.edit')
  /*
   * PUBLIER = ACTE GLOBAL (4.8, §2-7). Deux conditions, jamais une seule :
   *   · la permission `time.schedule.publish` — aveugle au périmètre ;
   *   · `scope.can_publish`, tranché par le SERVEUR (permission effective ET
   *     autorité globale). Un responsable d'équipe détient la permission sans
   *     avoir l'autorité : le bouton n'existe pas pour lui — jamais grisé.
   * Tant que le serveur n'expose pas le champ, il n'y a pas de bouton : on ne
   * promet pas une action qu'on ne sait pas autorisée.
   */
  const hasPublishPerm = !perms.loading && perms.permissions.includes('time.schedule.publish')
  const canPublish = hasPublishPerm && d?.scope?.can_publish === true
  /* L'amplitude d'affichage n'est pas une publication : elle reste liée à la seule permission. */
  const canSetPolicy = hasPublishPerm
  // Absences : l'onglet n'existe que si le droit existe (jamais un bouton grisé) ; annuler = droit distinct.
  const canLeave = !perms.loading && perms.permissions.includes('leave.create_for_others')
  // Annuler = `leave.cancel` ; un approbateur (`leave.approve`) garde ce pouvoir sur la route /cancel — le périmètre de la personne est tranché côté serveur (403 OUT_OF_SCOPE).
  const canCancelLeave = !perms.loading && (perms.permissions.includes('leave.cancel') || perms.permissions.includes('leave.approve'))
  const gridRef = useRef<HTMLDivElement>(null)
  // Le champ « Rechercher un salarié… » : « Tout afficher » y rend le focus, jamais à <body>.
  const qRef = useRef<HTMLInputElement>(null)

  /*
   * CYCLE DE VIE DU MENU CONTEXTUEL (4.8, §34-37).
   *
   * L'élément d'où il a été ouvert est mémorisé pour lui RENDRE le focus
   * quand on renonce (Escape) — sinon le focus retombe sur <body> et les
   * flèches de la grille meurent. On ne le lui reprend pas quand la
   * fermeture vient d'un clic ailleurs : ce clic a déjà donné le focus à
   * quelque chose, le voler serait pire que de ne rien faire.
   */
  const ctxOrigin = useRef<HTMLElement | null>(null)
  const closeCtx = useCallback(() => {
    setCtx(null)
    const el = ctxOrigin.current
    ctxOrigin.current = null
    if (!el) return
    requestAnimationFrame(() => {
      const now = document.activeElement
      if ((now === null || now === document.body) && document.contains(el)) el.focus()
    })
  }, [])

  const load = useCallback(() => {
    // Les soirées sont normalisées À L'ENTRÉE : `d.soirees` est TOUJOURS un tableau,
    // vide quand l'acteur n'a pas `soiree.read` ou quand le serveur ne sert pas le champ.
    apiGet<WeekData>(`/api/v1/hr/planning/week?week=${week}`).then((r) => {
      if ('shifts' in r) setD({ ...r, soirees: readSoirees((r as { soirees?: unknown }).soirees) })
    }).catch(() => undefined)
    apiGet<DiffData>(`/api/v1/hr/planning/diff?week=${week}`).then((r) => { if ('diff' in r) setDiff(r) }).catch(() => undefined)
    setCtxTick((t) => t + 1)
  }, [week])
  useEffect(() => { load() }, [load])

  const monthFirst = `${refDay.slice(0, 7)}-01`
  const monthDays = new Date(Date.UTC(Number(refDay.slice(0, 4)), Number(refDay.slice(5, 7)), 0)).getUTCDate()
  const monthLast = `${refDay.slice(0, 7)}-${String(monthDays).padStart(2, '0')}`
  useEffect(() => {
    if (view !== 'month') return
    apiGet<RangeData>(`/api/v1/hr/planning/range?from=${mondayOfYmd(monthFirst)}&to=${mondayOfYmd(monthLast)}`).then((r) => { if ('shifts' in r) setRange(r) }).catch(() => undefined)
  }, [view, monthFirst, monthLast, d]) // eslint-disable-line react-hooks/exhaustive-deps

  // Seul ce qui diffère de l'ouverture nue s'écrit : semaine, salariés, aujourd'hui. La densité
  // reste dans le navigateur (localStorage, relu au montage). Ouvert sans réglage, le planning
  // garde donc l'adresse nue, celle que l'étoile et la barre des favoris retrouvent au caractère près.
  useEffect(() => {
    const p = new URLSearchParams()
    if (view !== 'week') p.set('view', view)
    if (mode !== 'employees') p.set('mode', mode)
    if (refDay !== todayYmd()) p.set('date', refDay)
    if (teamFilter) p.set('team', teamFilter)
    if (positionFilter) p.set('position', positionFilter)
    if (roomFilter) p.set('room', roomFilter)
    if (query) p.set('q', query)
    if (personFilter) p.set('personne', personFilter)
    if (!layers.unassigned) p.set('hideUnassigned', '1')
    if (inspect) p.set(inspect.kind, inspect.id)
    /* La réécriture commune des écrans (`adresse-ecran.ts`) : l'état d'historique
       ({ lcIdx }) du routeur est recopié, sinon le retour intelligent retombe sur
       le repli ; rien n'est réécrit si une navigation vient de partir ; et
       l'adresse complète est NOTÉE. Sans cette note, l'étoile des favoris
       (`useAdresseCourante`) gardait l'adresse d'arrivée : passer à Paul par
       « Aller à un salarié… », puis épingler, épinglait encore le planning de Léa. */
    remplacerRecherche(CHEMIN_PLANNING, p)
    try { localStorage.setItem('gestion.planning.density', density); localStorage.setItem('gestion.planning.layers', JSON.stringify(layers)) } catch { /* privé */ }
  }, [view, mode, refDay, density, teamFilter, positionFilter, roomFilter, query, personFilter, layers, inspect])

  /*
   * SMARTBACK — POSITION DE LA GRILLE (§58).
   *
   * L'URL porte déjà semaine, vue, mode, densité, filtres, recherche et
   * inspector : revenir d'une soirée restaure tout cela (vérifié : l'effet
   * d'URL ci-dessus les écrit tous, et l'état initial les relit tous).
   * Ce qu'elle ne porte PAS, c'est le défilement de `.pl-gridwrap` — un
   * conteneur défilant à part, que la restauration de scroll du shell (qui ne
   * connaît que le défilement de PAGE, `AppShell`) n'atteint pas. On le
   * mémorise donc ici, par (vue, mode, semaine ou jour), dans
   * `sessionStorage` : la position survit au retour arrière et à un
   * rechargement de l'onglet, et meurt avec l'onglet — jamais une position
   * d'il y a trois semaines rejouée à l'ouverture. Le planning d'une
   * personne a sa propre clé : il n'écrase pas la position de la grille
   * complète.
   */
  const scrollKey = `gestion.planning.scroll:${view}:${mode}:${view === 'week' ? week : view === 'day' ? refDay : refDay.slice(0, 7)}:${personFilter || 'tous'}`
  useEffect(() => {
    const host = gridRef.current?.querySelector<HTMLElement>('.pl-gridwrap')
    if (!host) return
    try {
      const raw = sessionStorage.getItem(scrollKey)
      if (raw) {
        const pos = JSON.parse(raw) as { x?: unknown; y?: unknown }
        if (typeof pos.x === 'number') host.scrollLeft = pos.x
        if (typeof pos.y === 'number') host.scrollTop = pos.y
      }
    } catch { /* stockage privé ou valeur illisible : on repart en haut à gauche */ }
    const onScroll = () => {
      try { sessionStorage.setItem(scrollKey, JSON.stringify({ x: host.scrollLeft, y: host.scrollTop })) } catch { /* privé */ }
    }
    host.addEventListener('scroll', onScroll, { passive: true })
    return () => host.removeEventListener('scroll', onScroll)
  }, [scrollKey, d])

  const runUndoAction = useCallback(async (a: UndoAction) => { await a.run(); load() }, [load])
  const pushUndo = (a: UndoAction) => toast('ok', a.label, { action: { label: 'ANNULER', onAction: () => void runUndoAction(a) } })

  const teams = useMemo(() => [...new Set((d?.people ?? []).flatMap((p) => p.teams))].sort(), [d])
  const byPerson = useMemo(() => {
    const m = new Map<string, Shift[]>()
    for (const s of d?.shifts ?? []) { const k = s.person_id ?? UNASSIGNED; const arr = m.get(k) ?? []; arr.push(s); m.set(k, arr) }
    return m
  }, [d])
  const verdictsByShift = useMemo(() => {
    const m = new Map<string, Verdict[]>()
    for (const v of d?.verdicts ?? []) for (const id of v.shift_ids) { const arr = m.get(id) ?? []; arr.push(v); m.set(id, arr) }
    return m
  }, [d])
  const verdictsByPerson = useMemo(() => {
    const m = new Map<string, Verdict[]>()
    for (const v of d?.verdicts ?? []) { const arr = m.get(v.person_id) ?? []; arr.push(v); m.set(v.person_id, arr) }
    return m
  }, [d])
  const diffByShift = useMemo(() => new Map((diff?.diff ?? []).map((x) => [x.shift_id, x.kind])), [diff])

  const matchesFilters = useCallback((s: Shift): boolean => {
    if (positionFilter && s.position_id !== positionFilter) return false
    if (roomFilter && s.location_id !== roomFilter) return false
    return true
  }, [positionFilter, roomFilter])

  const people = useMemo(() => {
    let list = d?.people ?? []
    if (teamFilter) list = list.filter((p) => p.teams.includes(teamFilter))
    if (query.trim()) { const q = query.trim().toLowerCase(); list = list.filter((p) => p.display_name.toLowerCase().includes(q)) }
    // Une égalité, jamais `includes` : l'identifiant d'une personne n'en désigne pas une autre.
    if (personFilter) list = list.filter((p) => p.person_id === personFilter)
    if (positionFilter || roomFilter) list = list.filter((p) => (byPerson.get(p.person_id) ?? []).some(matchesFilters))
    return list
  }, [d, teamFilter, query, personFilter, positionFilter, roomFilter, byPerson, matchesFilters])

  const unassignedShifts = useMemo(() => (byPerson.get(UNASSIGNED) ?? []).filter(matchesFilters), [byPerson, matchesFilters])
  // Les totaux d'en-tête suivent la personne filtrée : « 1 salarié » dit SES heures,
  // et seulement si SA ligne est affichée. Les shifts servis ne suivent que le
  // périmètre, pas l'état RH : une personne sortie des effectifs garde les siens,
  // le bandeau dit « n’apparaît pas », la grille est vide, l'en-tête ne compte rien.
  const personneAffichee = !!personFilter && people.some((p) => p.person_id === personFilter)
  const dayTotals = useMemo(() => {
    const t = [0, 0, 0, 0, 0, 0, 0]
    for (const s of d?.shifts ?? []) if (s.person_id && matchesFilters(s) && (!personFilter || (personneAffichee && s.person_id === personFilter))) { const i = dayIndex(week, s.starts_at); if (i >= 0) t[i]! += s.paid_minutes }
    return t
  }, [d, week, matchesFilters, personFilter, personneAffichee])
  const weekTotal = dayTotals.reduce((a, b) => a + b, 0)

  /* ─── API ─── */
  const apiCreate = async (input: Record<string, unknown>): Promise<{ ok: boolean; shift?: Shift; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }> => {
    const r = await adminMutate<{ error?: string; message?: string; shift?: Shift; warnings?: Verdict[]; blocks?: Verdict[] }>('POST', '/api/v1/hr/planning/shifts', input)
    if (r.status >= 400) return { ok: false, message: r.data.message ?? 'Refusé.', ...(r.data.blocks ? { blocks: r.data.blocks } : {}) }
    return { ok: true, ...(r.data.shift !== undefined ? { shift: r.data.shift } : {}), ...(r.data.warnings !== undefined ? { warnings: r.data.warnings } : {}) }
  }
  const apiUpdate = async (id: string, patch: Record<string, unknown>): Promise<{ ok: boolean; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }> => {
    const r = await adminMutate<{ error?: string; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }>('PUT', `/api/v1/hr/planning/shifts/${id}`, patch)
    if (r.status >= 400) return { ok: false, message: r.data.message ?? 'Refusé.', ...(r.data.blocks ? { blocks: r.data.blocks } : {}) }
    return { ok: true, ...(r.data.warnings !== undefined ? { warnings: r.data.warnings } : {}) }
  }
  const apiDelete = async (id: string) => adminMutate<{ error?: string; shift?: Shift }>('DELETE', `/api/v1/hr/planning/shifts/${id}`)
  const surfaceWarnings = (w?: Verdict[]) => { if (w && w.length > 0) toast('ok', `Enregistré — à noter : ${w[0]!.message}`) }

  const moveShifts = async (ids: string[], dayDelta: number, targetPerson: string | null | undefined, copy: boolean) => {
    if (!d) return
    const shifts = ids.map((id) => d.shifts.find((s) => s.id === id)).filter((s): s is Shift => !!s)
    if (shifts.length === 0) return
    const createdIds: string[] = []
    const inverses: { id: string; patch: Record<string, unknown> }[] = []
    let blockedToUnassigned = false
    for (const s of shifts) {
      const starts = shiftInstantByDays(s.starts_at, dayDelta)
      const ends = shiftInstantByDays(s.ends_at, dayDelta)
      const person = targetPerson !== undefined && shifts.length === 1 ? targetPerson : s.person_id
      if (copy) {
        const r = await apiCreate({ person_id: person, starts_at: starts, ends_at: ends, break_minutes: s.break_minutes, break_paid: s.break_paid, position_id: s.position_id, function_id: s.function_id, location_id: s.location_id, notes: s.notes, source: 'COPY' })
        if (!r.ok) { toast('error', `Copie impossible : ${r.message ?? 'refusée.'}`); continue }
        surfaceWarnings(r.warnings); if (r.shift) createdIds.push(r.shift.id)
      } else {
        const r = await apiUpdate(s.id, { person_id: person, starts_at: starts, ends_at: ends })
        if (!r.ok) {
          if (r.blocks && r.blocks.length && person) {
            const r2 = await apiUpdate(s.id, { person_id: null, starts_at: starts, ends_at: ends })
            if (r2.ok) { blockedToUnassigned = true; inverses.push({ id: s.id, patch: { person_id: s.person_id, starts_at: s.starts_at, ends_at: s.ends_at } }); continue }
          }
          toast('error', `Déplacement impossible : ${r.message ?? 'refusé.'}`); continue
        }
        surfaceWarnings(r.warnings); inverses.push({ id: s.id, patch: { person_id: s.person_id, starts_at: s.starts_at, ends_at: s.ends_at } })
      }
    }
    const n = copy ? createdIds.length : inverses.length
    if (n > 0) {
      const who = targetPerson && shifts.length === 1 ? ` · ${nameOf(targetPerson)}` : ''
      const label = blockedToUnassigned ? 'Placé en non assignés (non conforme pour ce salarié)' : copy ? `${n > 1 ? `${n} shifts copiés` : 'Shift copié'}${who}` : `${n > 1 ? `${n} shifts déplacés` : 'Shift déplacé'}${who}`
      pushUndo({ label, run: async () => { if (copy) { for (const id of createdIds) await apiDelete(id) } else { for (const inv of inverses) await apiUpdate(inv.id, inv.patch) } } })
    }
    setSelection(new Set()); load()
  }

  const assignToPerson = async (shiftId: string, personId: string | null) => {
    if (!d) return
    const s = d.shifts.find((x) => x.id === shiftId); if (!s) return
    const r = await apiUpdate(shiftId, { person_id: personId })
    if (!r.ok) { toast('error', `Assignation impossible : ${r.message ?? 'refusée.'}`); return }
    surfaceWarnings(r.warnings)
    pushUndo({ label: personId ? `Shift assigné · ${nameOf(personId)}` : 'Shift désassigné', run: async () => { await apiUpdate(shiftId, { person_id: s.person_id }) } })
    load()
  }

  const deleteShifts = async (ids: string[]) => {
    if (!d) return
    const removed = ids.map((id) => d.shifts.find((s) => s.id === id)).filter((s): s is Shift => !!s)
    for (const id of ids) await apiDelete(id)
    if (inspect?.kind === 'shift' && ids.includes(inspect.id)) setInspect(null)
    pushUndo({ label: `${removed.length > 1 ? `${removed.length} shifts supprimés` : 'Shift supprimé'}`, run: async () => { for (const s of removed) await apiCreate({ person_id: s.person_id, starts_at: s.starts_at, ends_at: s.ends_at, break_minutes: s.break_minutes, break_paid: s.break_paid, position_id: s.position_id, function_id: s.function_id, location_id: s.location_id, notes: s.notes }) } })
    setSelection(new Set()); load()
  }

  const bulkSetField = async (ids: string[], patch: Record<string, unknown>, label: string) => {
    if (!d) return
    const inverses: { id: string; patch: Record<string, unknown> }[] = []
    for (const id of ids) {
      const s = d.shifts.find((x) => x.id === id); if (!s) continue
      const before: Record<string, unknown> = {}
      for (const k of Object.keys(patch)) before[k] = (s as unknown as Record<string, unknown>)[k]
      const r = await apiUpdate(id, patch); if (r.ok) inverses.push({ id, patch: before })
    }
    if (inverses.length) pushUndo({ label, run: async () => { for (const inv of inverses) await apiUpdate(inv.id, inv.patch) } })
    setSelection(new Set()); load()
  }

  /* ─── Clavier ─── */
  const hoveredCell = useRef<{ personId: string | null; day: number } | null>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      // Escape quand un menu est ouvert : la pile d'overlays le sert en premier
      // (elle stoppe la propagation avant `window`) — ici on ne voit donc plus
      // que les Escape « grille », qui vident la sélection et l'inspector.
      if (e.key === 'Escape') { setSelection(new Set()); closeCtx(); if (!editor) setInspect(null); return }
      if (!canEdit) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && selection.size > 0) {
        setClipboard((d?.shifts ?? []).filter((s) => selection.has(s.id)))
        toast('ok', `${selection.size} shift${selection.size > 1 ? 's' : ''} copié${selection.size > 1 ? 's' : ''} — Ctrl+V sur une cellule.`); e.preventDefault(); return
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && clipboard.length > 0 && hoveredCell.current) {
        const anchor = Math.min(...clipboard.map((s) => dayIndex(week, s.starts_at)))
        const delta = hoveredCell.current.day - anchor
        void moveShifts(clipboard.map((s) => s.id), delta, clipboard.length === 1 ? hoveredCell.current.personId : undefined, true)
        e.preventDefault(); return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selection.size > 0) { void deleteShifts([...selection]); e.preventDefault() }
    }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  })

  /* ─── Contexte (absences + disponibilités) ─── */
  const [pctx, setPctx] = useState<PlanningContext | null>(null)
  useEffect(() => {
    let alive = true
    // apiGet RÉSOUT un 403/404/500 avec { error } : readContext refuse tout ce qui n'a pas la forme attendue.
    apiGet<unknown>(`/api/v1/hr/planning/context?week=${week}`).then((r) => { if (alive) setPctx(readContext(r)) }).catch(() => { if (alive) setPctx(null) })
    return () => { alive = false }
  }, [week, ctxTick])
  /** Toutes les absences (approuvées + en attente) d'un salarié un jour civil donné — un tableau, plusieurs absences possibles. */
  const leavesOn = useCallback((personId: string | null, ymd: string): LeaveRec[] => { if (!pctx || !personId) return []; return pctx.leaves.filter((l) => l.person_id === personId && l.starts_on <= ymd && l.ends_on >= ymd) }, [pctx])
  const leavesFor = useCallback((personId: string | null, day: number): LeaveRec[] => leavesOn(personId, addDays(week, day)), [leavesOn, week])
  const availFor = useCallback((personId: string | null, day: number) => { if (!pctx || !personId) return null; return pctx.availability?.[addDays(week, day)]?.[personId] ?? null }, [pctx, week])
  // Vue Mois : le contexte est hebdomadaire — une requête par lundi du mois (4 à 6), fusionnées par identifiant.
  const [monthLeaves, setMonthLeaves] = useState<LeaveRec[]>([])
  useEffect(() => {
    if (view !== 'month') return
    let alive = true
    const mondays: string[] = []
    for (let m = mondayOfYmd(monthFirst); m <= monthLast; m = addDays(m, 7)) mondays.push(m)
    Promise.all(mondays.map((w) => apiGet<unknown>(`/api/v1/hr/planning/context?week=${w}`).then(readContext).catch(() => null)))
      .then((rs) => {
        if (!alive) return
        const seen = new Set<string>(); const all: LeaveRec[] = []
        for (const r of rs) for (const l of r?.leaves ?? []) if (!seen.has(l.id)) { seen.add(l.id); all.push(l) }
        setMonthLeaves(all)
      })
    return () => { alive = false }
  }, [view, monthFirst, monthLast, ctxTick])

  /* ─── Drag & drop ─── */
  const drag = useRef<{ ids: string[]; anchorDay: number; startX: number; startY: number; started: boolean; ghost: HTMLDivElement | null; label: string; alt: boolean; pointerType: string } | null>(null)
  const [dropPreview, setDropPreview] = useState<{ key: string; lines: string[]; bad: boolean; copy: boolean } | null>(null)
  const [dropChoice, setDropChoice] = useState<{ x: number; y: number; ids: string[]; delta: number; person: string | null | undefined } | null>(null)

  const nameOf = (pid: string | null) => pid ? humanName(d?.people.find((p) => p.person_id === pid)?.display_name ?? '—') : 'Non assigné'

  const onChipPointerDown = (e: React.PointerEvent, shift: Shift) => {
    if (!canEdit || e.button !== 0) return
    if ((e.target as HTMLElement).closest('.pl-chip__handle')) return
    if (e.ctrlKey || e.metaKey) { setSelection((sel) => { const n = new Set(sel); if (n.has(shift.id)) n.delete(shift.id); else n.add(shift.id); return n }); e.preventDefault(); return }
    const ids = selection.has(shift.id) ? [...selection] : [shift.id]
    drag.current = { ids, anchorDay: dayIndex(week, shift.starts_at), startX: e.clientX, startY: e.clientY, started: false, ghost: null, label: `${parisHm(shift.starts_at)}–${parisHm(shift.ends_at)}`, alt: e.altKey, pointerType: e.pointerType }
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* levé */ }
  }
  const onChipPointerMove = (e: React.PointerEvent) => {
    const st = drag.current; if (!st || !d) return
    st.alt = e.altKey
    if (!st.started) {
      if (Math.hypot(e.clientX - st.startX, e.clientY - st.startY) < 6) return
      st.started = true
      const g = document.createElement('div'); g.className = 'pl-dragghost'; g.textContent = st.ids.length > 1 ? `${st.ids.length} shifts` : st.label
      document.body.appendChild(g); st.ghost = g; document.body.classList.add('pl-dragging')
    }
    if (st.ghost) { st.ghost.style.left = `${e.clientX + 14}px`; st.ghost.style.top = `${e.clientY + 12}px`; st.ghost.setAttribute('data-copy', String(st.alt)) }
    const el = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-cell]') as HTMLElement | null
    if (!el) { setDropPreview(null); return }
    const day = Number(el.dataset['day']); const delta = day - st.anchorDay
    // Vue Postes (data-keep) : la cellule n'a pas de salarié, chaque shift garde le sien — seul le jour change.
    const keep = el.dataset['keep'] !== undefined
    const personRaw = el.dataset['person']
    const person: string | null = keep || personRaw === UNASSIGNED || personRaw === undefined ? null : personRaw
    // Aperçu de dépôt ANCRÉ dans la cellule cible (§36) : quoi → qui, avec verdicts.
    const moved = st.ids.map((id) => d.shifts.find((s) => s.id === id)).filter((s): s is Shift => !!s)
    const first = moved[0]
    const dayName = `${DAY_LABELS[day] ?? ''} ${new Date(`${addDays(week, day)}T12:00:00Z`).getUTCDate()}`
    const whereTo = keep ? `${moved.length === 1 && first && !first.person_id ? 'Non assigné' : 'même salarié'}, ${dayName}` : person ? nameOf(person) : 'Non assigné'
    const lines: string[] = []
    let bad = false
    if (moved.length === 1 && first) {
      lines.push([first.position_name ?? 'Poste non renseigné', first.location_code].filter(Boolean).join(' · '))
      lines.push(`${parisHm(shiftInstantByDays(first.starts_at, delta))}–${parisHm(first.ends_at)} → ${whereTo}`)
    } else lines.push(`${moved.length} shifts → ${whereTo}`)
    const av = availFor(person, day)
    // Une absence APPROUVÉE rend le dépôt non conforme ; une demande en attente n'est qu'un avertissement (rien n'est validé).
    for (const lv of leavesFor(person, day)) {
      if (lv.status === 'APPROVED') { lines.push(`⚠ ${lv.type_label} approuvé`); bad = true }
      else lines.push(`⚠ ${lv.type_label} — demande en attente`)
    }
    if (av?.status === 'UNAVAILABLE') { lines.push('✕ Indisponible ce jour'); bad = true }
    else if (av?.status === 'PARTIAL') lines.push('⚠ Partiellement disponible')
    else if (av?.status === 'AVAILABLE') lines.push('✓ Disponible')
    // Chevauchement RÉEL : les intervalles déplacés contre les autres shifts du salarié — deux shifts le même jour ne se chevauchent pas forcément.
    const overlaps = (pid: string | null, shifts: Shift[]): boolean => {
      if (!pid) return false
      const others = (byPerson.get(pid) ?? []).filter((o) => !st.ids.includes(o.id))
      return shifts.some((s) => {
        const starts = new Date(shiftInstantByDays(s.starts_at, delta)).getTime(); const ends = new Date(shiftInstantByDays(s.ends_at, delta)).getTime()
        return others.some((o) => new Date(o.starts_at).getTime() < ends && new Date(o.ends_at).getTime() > starts)
      })
    }
    // Un seul shift déposé sur un salarié change de titulaire ; sinon (sélection multiple, vue Postes) chacun garde le sien — comme moveShifts.
    const retarget = moved.length === 1 && !keep
    if (retarget ? overlaps(person, moved) : moved.some((s) => overlaps(s.person_id, [s]))) { lines.push('⚠ Chevauche un shift existant'); bad = true }
    setDropPreview({ key: keep ? `keep:${el.dataset['row'] ?? ''}:${day}` : `${personRaw ?? UNASSIGNED}:${day}`, lines, bad, copy: st.alt })
  }
  const onChipPointerUp = (e: React.PointerEvent, shift: Shift) => {
    const st = drag.current; drag.current = null; setDropPreview(null)
    document.body.classList.remove('pl-dragging'); st?.ghost?.remove()
    if (!st) return
    if (!st.started) { setInspect({ kind: 'shift', id: shift.id }); return }
    const el = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-cell]') as HTMLElement | null
    if (!el) return
    // Vue Postes (data-keep) : le salarié est CONSERVÉ (cible indéfinie → moveShifts garde s.person_id), seul le jour change.
    const keep = el.dataset['keep'] !== undefined
    const personRaw = el.dataset['person']
    const person: string | null = personRaw === UNASSIGNED || personRaw === undefined ? null : personRaw
    const day = Number(el.dataset['day'])
    const delta = day - st.anchorDay
    const single = st.ids.length === 1
    if (delta === 0 && !st.alt && (keep || (single && person === shift.person_id))) return
    const target = keep || !single ? undefined : person
    // Desktop : déplacement direct (Alt = copie). Touch : choix ancré, cible large (§7-8).
    if (st.pointerType === 'touch') { const r = el.getBoundingClientRect(); setDropChoice({ x: r.left + r.width / 2, y: r.bottom, ids: st.ids, delta, person: target }); return }
    void moveShifts(st.ids, delta, target, st.alt)
  }

  /* ─── Resize ─── */
  const resize = useRef<{ id: string; edge: 'start' | 'end'; baseIso: string; otherIso: string; startX: number } | null>(null)
  const [resizeHint, setResizeHint] = useState<{ id: string; text: string } | null>(null)
  const onHandleDown = (e: React.PointerEvent, shift: Shift, edge: 'start' | 'end') => {
    if (!canEdit) return; e.stopPropagation()
    resize.current = { id: shift.id, edge, baseIso: edge === 'start' ? shift.starts_at : shift.ends_at, otherIso: edge === 'start' ? shift.ends_at : shift.starts_at, startX: e.clientX }
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId) } catch { /* levé */ }
  }
  const onHandleMove = (e: React.PointerEvent) => {
    const st = resize.current; if (!st) return
    const next = addMinutesIso(st.baseIso, Math.round((e.clientX - st.startX) / 12) * 15)
    const [a, b] = st.edge === 'start' ? [next, st.otherIso] : [st.otherIso, next]
    if (new Date(a) >= new Date(b)) return
    setResizeHint({ id: st.id, text: `${parisHm(a)} → ${parisHm(b)} (${fmtH(Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60_000))})` })
  }
  const onHandleUp = async (e: React.PointerEvent, shift: Shift) => {
    const st = resize.current; resize.current = null; setResizeHint(null); if (!st) return
    const steps = Math.round((e.clientX - st.startX) / 12); if (steps === 0) return
    const next = addMinutesIso(st.baseIso, steps * 15)
    const patch = st.edge === 'start' ? { starts_at: next } : { ends_at: next }
    const before = st.edge === 'start' ? { starts_at: shift.starts_at } : { ends_at: shift.ends_at }
    const r = await apiUpdate(shift.id, patch); if (!r.ok) { toast('error', `Ajustement impossible : ${r.message ?? 'refusé.'}`); return }
    surfaceWarnings(r.warnings); pushUndo({ label: 'Horaire ajusté', run: async () => { await apiUpdate(shift.id, before) } }); load()
  }

  /* ─── Publication ─── */
  const openPublish = async () => { const r = await apiGet<PublishPreview>(`/api/v1/hr/planning/publish-preview?week=${week}`); if ('diff' in r) setPublish(r) }
  const doPublish = async () => {
    const r = await adminMutate<{ error?: string; message?: string; version?: number; notified?: number; affected?: number }>('POST', '/api/v1/hr/planning/publish', { week })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Publication refusée.'); return }
    toast('ok', `Planning v${r.data.version} publié — ${r.data.affected} salarié${(r.data.affected ?? 0) > 1 ? 's' : ''} concerné${(r.data.affected ?? 0) > 1 ? 's' : ''}, ${r.data.notified} notifié${(r.data.notified ?? 0) > 1 ? 's' : ''}.`)
    setPublish(null); load()
  }

  const blocks = (d?.verdicts ?? []).filter((v) => v.severity === 'BLOCK')
  const warns = (d?.verdicts ?? []).filter((v) => v.severity === 'WARNING')
  const infos = (d?.verdicts ?? []).filter((v) => v.severity === 'INFO')
  const fmtSnap = (s: SnapRow | null) => s ? `${DAY_LABELS[Math.max(0, dayIndex(week, s.starts_at))]} ${parisHm(s.starts_at)}–${parisHm(s.ends_at)}` : '—'

  const todayIdx = dayIndex(week, new Date().toISOString())
  const weekLabel = `${new Date(`${week}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${new Date(`${addDays(week, 6)}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`
  const dayLabel = new Date(`${refDay}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
  const monthLabel = new Date(`${refDay}T12:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  const navLabel = view === 'day' ? dayLabel : view === 'month' ? monthLabel : weekLabel
  const navBy = (n: number) => setRefDay(addDays(refDay, view === 'day' ? n : view === 'month' ? n * 30 : n * 7))

  const positionItems: ComboItem[] = (d?.positions ?? []).map((p) => ({ value: p.id, label: p.name, dot: `lc-dot--${shiftTone({ position_id: p.id, position_color: p.color })}` }))
  const teamItems: ComboItem[] = teams.map((t) => ({ value: t, label: t }))
  const roomItems: ComboItem[] = (d?.locations ?? []).map((l) => ({ value: l.id, label: l.name, hint: l.code }))
  const employeeItems: ComboItem[] = (d?.people ?? []).map((p) => ({ value: p.person_id, label: humanName(p.display_name) }))
  const activeChips: { label: string; clear: () => void }[] = [
    ...(teamFilter ? [{ label: `Équipe ${teamFilter}`, clear: () => setTeamFilter('') }] : []),
    ...(positionFilter ? [{ label: d?.positions.find((p) => p.id === positionFilter)?.name ?? 'Poste', clear: () => setPositionFilter('') }] : []),
    ...(roomFilter ? [{ label: d?.locations.find((l) => l.id === roomFilter)?.name ?? 'ROOM', clear: () => setRoomFilter('') }] : []),
  ]
  /* Le nom du bandeau vient de l'effectif servi, jamais de l'adresse. */
  const bandeau = useMemo(() => bandeauPersonne(personFilter, d?.people ?? null), [personFilter, d])
  const toutAfficher = () => { setPersonFilter(''); requestAnimationFrame(() => qRef.current?.focus()) }
  /* Planning d'une personne : la vue Postes (Semaine, Jour) montre tout
     l'effectif, les autres vues seulement SA ligne. Changer de mode ou de vue
     vide donc la sélection : des shifts d'autres salariés, choisis en vue
     Postes, ne restent pas sélectionnés sous la grille d'une seule personne
     (Supprimer, Désassigner, Poste…, ROOM… les auraient visés). */
  const changerMode = (m: Mode) => { if (personFilter) setSelection(new Set()); setMode(m) }
  const changerVue = (v: View) => { if (personFilter) setSelection(new Set()); setView(v) }
  const jumpTo = (personId: string | null) => {
    if (!personId) return
    // Planning d'une personne : en mode Employés, les autres lignes ne sont
    // pas rendues, faire défiler n'aurait aucun effet. « Aller à un salarié… »
    // ouvre donc SON planning, et vide la sélection, comme changerMode et
    // changerVue : les shifts choisis chez l'ancienne personne ne restent pas
    // sélectionnés hors de la grille. Les filtres d'équipe, de poste, de ROOM
    // et ?q= ne la vident pas (comportement d'avant ce lot) : un shift qu'ils
    // masquent peut rester sélectionné, sous le droit et le périmètre du serveur.
    if (personFilter) { if (personId !== personFilter) { setSelection(new Set()); setPersonFilter(personId) } return }
    const el = gridRef.current?.querySelector(`[data-personrow="${personId}"]`) as HTMLElement | null
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' }); el?.classList.add('pl-row--flash'); setTimeout(() => el?.classList.remove('pl-row--flash'), 1200)
  }
  /*
   * CIBLAGE DÉTERMINISTE (§47) — UN seul gestionnaire, posé sur la surface.
   *
   * `closest('.pl-chip, .pl-day__bar')` d'abord : un clic droit sur une carte
   * est un clic droit sur CETTE carte, où qu'il tombe dedans. Sinon
   * `closest('[data-cell]')` : le fond d'une cellule, occupée ou non.
   *
   * §51 : `preventDefault()` UNIQUEMENT quand un menu Gestion prend la cible
   * en charge. Ailleurs — vue Mois (§50 : la cellule ne porte pas de couple
   * personne/jour fiable), en-têtes, toolbar, inspector — le menu du
   * navigateur reste disponible, avec « Ouvrir dans un nouvel onglet ».
   */
  const openContext = (e: React.MouseEvent<HTMLElement>) => {
    if (view === 'month') return
    // Préférence « Menu clic droit » coupée, ou Maj + clic droit : le menu du navigateur.
    if (!menuGestion || e.shiftKey) return
    const t = e.target as HTMLElement
    const card = t.closest('.pl-chip, .pl-day__bar') as HTMLElement | null
    if (card) {
      const id = card.dataset['shift']
      const s = id ? (d?.shifts ?? []).find((x) => x.id === id) : undefined
      if (!s) return
      e.preventDefault()
      ctxOrigin.current = card
      setCtx({ x: e.clientX, y: e.clientY, target: { kind: 'shift', shift: s } })
      return
    }
    const cell = t.closest('[data-cell]') as HTMLElement | null
    const date = cell?.dataset['date']
    if (!cell || !date) return
    const position = cell.dataset['ctx'] === 'position'
    const raw = cell.dataset['person']
    const unassigned = raw === undefined || raw === UNASSIGNED
    // Une cellule anonyme (ligne Non assignés, vue Postes) n'accepte pas d'absence :
    // sans droit d'édition il ne resterait RIEN à proposer — on rend alors la main
    // au navigateur plutôt que d'ouvrir un menu vide.
    const anonymous = position || unassigned
    if (!canEdit && !(canLeave && !anonymous)) return
    e.preventDefault()
    ctxOrigin.current = cell
    if (position) {
      setCtx({ x: e.clientX, y: e.clientY, target: { kind: 'position', positionId: cell.dataset['position'] || null, day: date } })
      return
    }
    setCtx({ x: e.clientX, y: e.clientY, target: { kind: 'cell', personId: unassigned ? null : raw, day: date, unassigned } })
  }

  /*
   * COLLER ICI (§44) — une COPIE : le serveur émet un nouvel identifiant et
   * refait ses contrôles légaux. Le presse-papier interne porte les shifts
   * eux-mêmes, pas des identifiants : coller reste possible après avoir
   * changé de semaine. Plusieurs shifts gardent leur écart de jours ; un seul
   * prend la personne de la cellule visée.
   */
  const pasteInto = async (personId: string | null, day: string, positionId?: string | null) => {
    if (clipboard.length === 0) return
    const anchor = clipboard.map((s) => parisYmd(s.starts_at)).sort()[0]!
    // Le décalage est calculé UNE fois, depuis le shift le plus tôt : les autres
    // conservent leur écart relatif (une paire lundi/mercredi reste une paire).
    const move = daysBetween(anchor, day)
    const created: string[] = []
    for (const s of clipboard) {
      const r = await apiCreate({
        person_id: clipboard.length === 1 ? personId : s.person_id,
        starts_at: shiftInstantByDays(s.starts_at, move), ends_at: shiftInstantByDays(s.ends_at, move),
        break_minutes: s.break_minutes, break_paid: s.break_paid,
        // Coller sur une LIGNE DE POSTE range dans CE poste : sinon le besoin
        // atterrissait dans la ligne du poste copié, ailleurs sur la grille.
        position_id: positionId !== undefined ? positionId : s.position_id,
        function_id: s.function_id, location_id: s.location_id, notes: s.notes, source: 'COPY',
      })
      if (!r.ok) { toast('error', `Collage impossible : ${r.message ?? 'refusé.'}`); continue }
      surfaceWarnings(r.warnings); if (r.shift) created.push(r.shift.id)
    }
    if (created.length > 0) pushUndo({ label: created.length > 1 ? `${created.length} shifts collés` : `Shift collé · ${nameOf(personId)}`, run: async () => { for (const id of created) await apiDelete(id) } })
    load()
  }
  /*
   * FERMETURE SUR CHANGEMENT DE CONTEXTE (§34) — un menu contextuel décrit
   * une cellule précise d'une semaine précise. Dès que ce décor bouge
   * (semaine, date, vue, mode, route) ou qu'une autre surface prend la main
   * (composer, inspector, publication, répétition), il ne décrit plus rien :
   * il se ferme. Le clic extérieur, le clic droit ailleurs, Escape, le
   * défilement et le redimensionnement sont traités par la primitive.
   */
  useEffect(() => {
    // On ne passe PAS par `closeCtx` : rendre le focus n'a pas de sens quand la
    // fermeture vient d'un changement de décor. Mais l'origine doit être
    // oubliée, sinon un Escape ultérieur posait l'anneau de focus sur une
    // cellule que l'utilisateur n'avait pas désignée (même ligne, autre date).
    setCtx(null)
    ctxOrigin.current = null
  }, [view, mode, refDay, week, path, editor, inspect, publish, repeatFor, versionsOpen, tplOpen, clearOpen])

  /* Soirées de la semaine indexées par NUIT COMMERCIALE (§55) : la clé n'est pas
     l'instant civil de début mais la nuit à laquelle la soirée appartient — c'est
     le serveur qui l'a calculée (`businessNightOf`), le planning ne la redevine pas. */
  const soireesByNight = useMemo(() => {
    const m = new Map<string, Soiree[]>()
    for (const s of d?.soirees ?? []) { const a = m.get(s.night) ?? []; a.push(s); m.set(s.night, a) }
    return m
  }, [d])
  const soireesOn = useCallback((ymd: string) => soireesByNight.get(ymd) ?? [], [soireesByNight])

  const inspected = inspect?.kind === 'shift' ? d?.shifts.find((s) => s.id === inspect.id) ?? null : null
  const inspectedLeave = inspect?.kind === 'leave' ? pctx?.leaves.find((l) => l.id === inspect.id) ?? null : null
  // Auto-nettoyage : un shift supprimé / une absence annulée ou hors semaine ferme l'inspector — jamais un panneau orphelin.
  useEffect(() => { if (inspect?.kind === 'shift' && d && !d.shifts.some((s) => s.id === inspect.id)) setInspect(null) }, [d, inspect])
  useEffect(() => { if (inspect?.kind === 'leave' && pctx && !pctx.leaves.some((l) => l.id === inspect.id)) setInspect(null) }, [pctx, inspect])
  const openLeave = useCallback((id: string) => setInspect({ kind: 'leave', id }), [])

  /* §5 : sans autorité globale, l'état ne propose pas une action qu'on ne peut pas
     faire — il DIT qui la fera. « n modification(s) — publication par la Direction ». */
  const pending = d?.publication?.pending_changes ?? 0
  const pubState: { label: string; tone: 'idle' | 'ok' | 'warning' } = !d?.publication
    ? { label: 'Brouillon', tone: 'idle' }
    : pending > 0
      ? { label: canPublish ? `Modifié · ${pending} à publier` : `${pending} modification${pending > 1 ? 's' : ''} — publication par la Direction`, tone: 'warning' }
      : { label: `Publié v${d.publication.version}`, tone: 'ok' }

  const chipHandlers: ChipHandlers = { canEdit, density, showAlerts: layers.alerts, published: !!d?.publication, verdictsByShift, diffByShift, onPointerDown: onChipPointerDown, onPointerMove: onChipPointerMove, onPointerUp: onChipPointerUp, onHandleDown, onHandleMove, onHandleUp, resizeHint, onOpenEditor: (s) => setEditor({ shift: s, personId: s.person_id, day: parisYmd(s.starts_at) }), selectedId: inspected?.id ?? null }

  return (
    <main className={`lc-page lc-page--wide lc-stagger pl-page pl-page--${density}`}>
      <Trail />
      <SmartBack fallback="/equipe" />

      {/* ─── TOOLBAR niveau 1 ─── */}
      <header className="pl-bar">
        <div className="pl-bar__left">
          <h1 className="lc-title pl-bar__title"><CalendarDays size={20} aria-hidden="true" /> Planning</h1>
          <SegmentedControl<View> value={view} onChange={changerVue} ariaLabel="Vue" size="sm"
            options={[{ value: 'day', label: 'Jour', icon: <Calendar size={14} /> }, { value: 'week', label: 'Semaine', icon: <Columns3 size={14} /> }, { value: 'month', label: 'Mois', icon: <CalendarRange size={14} /> }]} />
          {view !== 'month' ? (
            <SegmentedControl<Mode> value={mode} onChange={changerMode} ariaLabel="Mode" size="sm"
              options={[{ value: 'employees', label: 'Employés', icon: <Users size={14} /> }, { value: 'positions', label: 'Postes', icon: <Briefcase size={14} /> }]} />
          ) : null}
          <div className="pl-period">
            <button className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Précédent" onClick={() => navBy(-1)}><ChevronLeft size={16} /></button>
            <DatePicker value={refDay} onPick={setRefDay} label={navLabel} highlightFrom={week} highlightTo={addDays(week, 6)} ariaLabel="Aller à une date" />
            <button className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Suivant" onClick={() => navBy(1)}><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="pl-bar__right">
          <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setRefDay(todayYmd())}>Aujourd’hui</button>
          <span className={`lc-pill lc-pill--${pubState.tone}`}>{pubState.label}</span>
          {(blocks.length + warns.length + infos.length) > 0 ? (
            <button className={`lc-btn lc-btn--ghost lc-btn--sm pl-verdictbtn${blocks.length ? ' pl-verdictbtn--block' : warns.length ? ' pl-verdictbtn--warn' : ''}`} onClick={() => setVerdictPanel(!verdictPanel)} aria-expanded={verdictPanel}>
              {blocks.length > 0 ? <ShieldAlert size={14} /> : warns.length > 0 ? <AlertTriangle size={14} /> : <Info size={14} />}
              {blocks.length > 0 ? `${blocks.length} blocage${blocks.length > 1 ? 's' : ''}` : warns.length > 0 ? `${warns.length} alerte${warns.length > 1 ? 's' : ''}` : `${infos.length} info${infos.length > 1 ? 's' : ''}`}
            </button>
          ) : null}
          <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setVersionsOpen(true)}><History size={14} /> Versions</button>
          <PlanningMenu canEdit={canEdit} onTemplates={() => setTplOpen(true)} onClear={() => setClearOpen(true)}
            onDuplicatePrev={async () => { const r = await adminMutate<{ error?: string; created?: number }>('POST', '/api/v1/hr/planning/duplicate-previous', { week }); if (r.status < 400) { toast('ok', `${r.data.created ?? 0} shift(s) copié(s) depuis la semaine précédente.`); load() } }} />
          {canPublish ? <button className="lc-btn lc-btn--primary" onClick={() => void openPublish()}><Send size={14} /> Publier</button> : null}
        </div>
      </header>

      {/* ─── TOOLBAR niveau 2 : filtres ─── */}
      <div className="pl-filters">
        <input ref={qRef} className="lc-input pl-filters__q" placeholder="Rechercher un salarié…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Combobox value={teamFilter || null} items={teamItems} onChange={(v) => setTeamFilter(v ?? '')} placeholder="Équipes" ariaLabel="Filtrer par équipe" clearable leading={<Users size={14} />} />
        <Combobox value={positionFilter || null} items={positionItems} onChange={(v) => setPositionFilter(v ?? '')} placeholder="Postes" ariaLabel="Filtrer par poste" clearable leading={<Briefcase size={14} />} />
        <Combobox value={roomFilter || null} items={roomItems} onChange={(v) => setRoomFilter(v ?? '')} placeholder="ROOM" ariaLabel="Filtrer par ROOM" clearable leading={<MapPin size={14} />} />
        <Combobox value={null} items={employeeItems} onChange={jumpTo} placeholder="Aller à un salarié…" ariaLabel="Aller à un salarié" />
        <DisplayOptions density={density} setDensity={setDensity} layers={layers} setLayers={setLayers} policy={d?.policy ?? null} canPolicy={canSetPolicy} onPolicySaved={load} />
        {activeChips.length > 0 ? (
          <span className="pl-chips">
            {activeChips.map((c, i) => <button key={i} className="pl-fchip" onClick={c.clear}>{c.label}<X size={12} /></button>)}
            <button className="pl-fchip pl-fchip--reset" onClick={() => { setTeamFilter(''); setPositionFilter(''); setRoomFilter('') }}>Réinitialiser</button>
          </span>
        ) : null}
      </div>

      {/* ─── PLANNING D'UNE PERSONNE (?personne=) : un filtre d'affichage, rien n'est écrit ─── */}
      {personFilter ? (
        <div className="pl-personbar" role="status">
          <UserRound size={15} aria-hidden="true" />
          <span className="pl-personbar__text">
            {/* Chaque libellé est une phrase finie : la mention Postes qui suit est une seconde phrase, lue à part (role="status"). */}
            {bandeau.etat === 'trouve'
              ? <>Planning de <strong>{humanName(bandeau.nom)}</strong>.</>
              : bandeau.etat === 'absent'
                ? 'Ce salarié n’apparaît pas dans votre planning : hors de votre périmètre, ou sorti des effectifs.'
                : 'Planning d’un salarié.'}
            {mode === 'positions' && view !== 'month' ? <span className="pl-personbar__hint"> La vue Postes montre tout l’effectif.</span> : null}
          </span>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={toutAfficher}>Tout afficher</button>
        </div>
      ) : null}

      {verdictPanel && d ? (
        <section className="lc-panel gd-panel pl-verdicts" aria-label="Règles">
          <div className="lc-panel__body">
            {[...blocks, ...warns, ...infos].map((v, i) => (
              <div key={i} className={`pl-verdict pl-verdict--${v.severity.toLowerCase()}`}>
                <span className="lc-stack">
                  <span><strong>{nameOf(v.person_id)}</strong> — {v.message}</span>
                  <span className="rh-assign__meta">Source : {v.source}{v.article ? ` · ${v.article}` : ''} · « {v.rule} »</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* ─── SURFACE + INSPECTOR (split intégré) ─── */}
      <div className={`pl-layout${inspected || inspectedLeave ? ' pl-layout--inspector' : ''}`}>
        {!d ? <div className="pl-skeleton" aria-hidden="true">{Array.from({ length: 7 }, (_, i) => <div key={i} className="pl-skeleton__row" />)}</div> : (
          // Un SEUL gestionnaire de clic droit pour toute la surface (§47) : le
          // ciblage se fait par `closest`, pas par un handler sur chaque carte.
          <div ref={gridRef} className={`pl-surface pl-surface--${density}`} onContextMenu={openContext}>
            {view === 'week' && mode === 'employees' ? (
              <WeekEmployees d={d} week={week} people={people} byPerson={byPerson} verdictsByPerson={verdictsByPerson} dayTotals={dayTotals} weekTotal={weekTotal} todayIdx={todayIdx}
                canEdit={canEdit} selection={selection} dropPreview={dropPreview} unassignedShifts={unassignedShifts} layers={layers} matchesFilters={matchesFilters}
                leavesFor={leavesFor} availFor={availFor} hoveredCell={hoveredCell} handlers={chipHandlers} setEditor={setEditor} inspectedLeaveId={inspectedLeave?.id ?? null} onOpenLeave={openLeave} soireesOn={soireesOn} />
            ) : view === 'week' && mode === 'positions' ? (
              <WeekPositions d={d} week={week} todayIdx={todayIdx} canEdit={canEdit} selection={selection} dropPreview={dropPreview} matchesFilters={matchesFilters} layers={layers} handlers={chipHandlers} setEditor={setEditor} leavesFor={leavesFor} soireesOn={soireesOn} />
            ) : view === 'day' ? (
              <DayView d={d} refDay={refDay} mode={mode} people={people} canEdit={canEdit} matchesFilters={matchesFilters} unassignedShifts={unassignedShifts} layers={layers} setEditor={setEditor} onInspect={(id) => setInspect({ kind: 'shift', id })}
                leavesOn={leavesOn} inspectedLeaveId={inspectedLeave?.id ?? null} onOpenLeave={openLeave} soirees={soireesOn(refDay)} />
            ) : (
              <MonthView range={range} refDay={refDay} monthFirst={monthFirst} monthDays={monthDays} teamFilter={teamFilter} query={query} personFilter={personFilter} positionFilter={positionFilter} roomFilter={roomFilter} setRefDay={setRefDay} setView={setView}
                leaves={layers.leaves ? monthLeaves : []} />
            )}
          </div>
        )}
        {inspected && d ? (
          <ShiftInspector shift={inspected} d={d} verdicts={verdictsByShift.get(inspected.id) ?? []} diffKind={diffByShift.get(inspected.id) ?? null} published={!!d.publication}
            canEdit={canEdit} onClose={() => setInspect(null)}
            onEdit={() => setEditor({ shift: inspected, personId: inspected.person_id, day: parisYmd(inspected.starts_at) })}
            onCopy={() => { setClipboard([inspected]); toast('ok', 'Copié — Ctrl+V sur une cellule.') }}
            onRepeat={() => setRepeatFor(inspected)} onUnassign={() => void assignToPerson(inspected.id, null)} onDelete={() => void deleteShifts([inspected.id])}
            onAssign={(pid) => void assignToPerson(inspected.id, pid)}
            dayLeaves={leavesOn(inspected.person_id, parisYmd(inspected.starts_at))} onOpenLeave={openLeave} />
        ) : inspectedLeave && d ? (
          <LeaveInspector key={inspectedLeave.id} leave={inspectedLeave} d={d} canCancel={canCancelLeave} onClose={() => setInspect(null)}
            onChanged={load} onOpenShift={(id) => { if (d.shifts.some((s) => s.id === id)) setInspect({ kind: 'shift', id }); else toast('error', 'Ce shift n’est pas dans la semaine affichée.') }} />
        ) : null}
      </div>

      {/* Bulk Action Bar (≠ Save Bar) — portal body */}
      {selection.size > 0 && canEdit && d ? createPortal(
        <div className="lc-bulkbar-zone">
          <div className="lc-bulkbar pl-bulkbar" role="toolbar" aria-label="Actions sur la sélection">
            <span className="lc-bulkbar__count"><span className="lc-dot" /> {selection.size} shift{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}</span>
            <span className="lc-bulkbar__actions">
              <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setClipboard(d.shifts.filter((s) => selection.has(s.id))); toast('ok', 'Copiés — Ctrl+V sur une cellule.') }}><Copy size={13} /> Copier</button>
              <Combobox value={null} items={positionItems} onChange={(v) => { if (v) void bulkSetField([...selection], { position_id: v }, 'Poste modifié sur la sélection') }} placeholder="Poste…" ariaLabel="Changer le poste" leading={<Briefcase size={13} />} className="pl-bulkbar__combo" />
              <Combobox value={null} items={roomItems} onChange={(v) => { if (v) void bulkSetField([...selection], { location_id: v }, 'ROOM modifiée sur la sélection') }} placeholder="ROOM…" ariaLabel="Changer la ROOM" leading={<MapPin size={13} />} className="pl-bulkbar__combo" />
              <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => void bulkSetField([...selection], { person_id: null }, 'Shifts désassignés')}><UserMinus size={13} /> Désassigner</button>
              <button className="lc-btn lc-btn--ghost lc-btn--sm lc-btn--danger-text" onClick={() => void deleteShifts([...selection])}><Trash2 size={13} /> Supprimer</button>
            </span>
            <button className="lc-btn lc-btn--ghost lc-bulkbar__clear" onClick={() => setSelection(new Set())} aria-label="Vider la sélection"><X size={15} /></button>
          </div>
        </div>, document.body,
      ) : null}

      {dropChoice ? (
        <Floating open anchorRef={{ current: floatingAnchor(dropChoice.x, dropChoice.y) }} onOutside={() => setDropChoice(null)} closeOnScroll className="lc-menu pl-dropmenu" placement="bottom-start">
          <ul className="lc-menu__list" role="menu">
            <li role="none"><button role="menuitem" className="lc-menu__item pl-dropmenu__item" onClick={() => { void moveShifts(dropChoice.ids, dropChoice.delta, dropChoice.person, false); setDropChoice(null) }}><ChevronRight size={16} /> Déplacer ici</button></li>
            <li role="none"><button role="menuitem" className="lc-menu__item pl-dropmenu__item" onClick={() => { void moveShifts(dropChoice.ids, dropChoice.delta, dropChoice.person, true); setDropChoice(null) }}><CopyPlus size={16} /> Copier ici</button></li>
          </ul>
        </Floating>
      ) : null}

      {/* MENU CONTEXTUEL (4.8) — une surface, trois contenus selon la cible.
          `closeOnScroll` : l'ancre est un POINT du viewport, elle ne suit pas
          la grille — au défilement le menu doit disparaître, pas rester. */}
      {ctx ? (
        <Floating open anchorRef={{ current: floatingAnchor(ctx.x, ctx.y) }} onOutside={closeCtx} closeOnScroll className="lc-menu pl-ctxmenu">
          {ctx.target.kind === 'shift' ? (() => {
            const s = ctx.target.shift
            return (
              <ul className="lc-menu__list" role="menu" aria-label="Actions du shift">
                <li className="lc-menu__section">{s.position_name ?? 'Poste non renseigné'} · {parisHm(s.starts_at)}–{parisHm(s.ends_at)}</li>
                <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setEditor({ shift: s, personId: s.person_id, day: parisYmd(s.starts_at) }); closeCtx() }}><Settings2 size={15} /> Modifier</button></li>
                <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setInspect({ kind: 'shift', id: s.id }); closeCtx() }}><Eye size={15} /> Inspecter</button></li>
                <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setClipboard([s]); toast('ok', 'Copié — Ctrl+V sur une cellule, ou « Coller ici ».'); closeCtx() }}><Copy size={15} /> Copier</button></li>
                <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setRepeatFor(s); closeCtx() }}><Repeat size={15} /> Répéter…</button></li>
                <li className="lc-menu__sep" role="separator" />
                {s.person_id ? <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { void assignToPerson(s.id, null); closeCtx() }}><UserMinus size={15} /> Désassigner</button></li> : null}
                <li role="none"><button role="menuitem" className="lc-menu__item lc-menu__item--danger" onClick={() => { void deleteShifts([s.id]); closeCtx() }}><Trash2 size={15} /> Supprimer</button></li>
              </ul>
            )
          })() : (() => {
            /* CELLULE (§46) et VUE POSTES (§49). Une absence appartient à une
               PERSONNE : ni la ligne « Non assignés » ni une cellule (poste, jour)
               n'en proposent — ce ne serait l'absence de personne. */
            const t = ctx.target
            const personId = t.kind === 'cell' ? t.personId : null
            const anonymous = t.kind === 'position' || (t.kind === 'cell' && t.unassigned)
            const who = anonymous
              ? (t.kind === 'position' ? (d?.positions.find((p) => p.id === t.positionId)?.name ?? 'Poste non renseigné') : 'Non assignés')
              : nameOf(personId)
            return (
              <ul className="lc-menu__list" role="menu" aria-label="Actions de la cellule">
                <li className="lc-menu__section">{who} · {fmtDateShort(t.day)}</li>
                {canEdit ? (
                  <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setEditor({ personId, day: t.day, ...(t.kind === 'position' ? { positionId: t.positionId } : {}) }); closeCtx() }}>
                    <Plus size={15} /> {anonymous ? 'Ajouter un besoin non assigné' : 'Ajouter un shift'}
                  </button></li>
                ) : null}
                {canLeave && !anonymous ? (
                  <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setEditor({ personId, day: t.day, tab: 'leave' }); closeCtx() }}>
                    <CalendarPlus size={15} /> Ajouter une absence
                  </button></li>
                ) : null}
                {canEdit && clipboard.length > 0 ? (
                  <>
                    <li className="lc-menu__sep" role="separator" />
                    <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { void pasteInto(personId, t.day, t.kind === 'position' ? t.positionId : undefined); closeCtx() }}>
                      <ClipboardPaste size={15} /> Coller ici{clipboard.length > 1 ? ` (${clipboard.length})` : ''}
                    </button></li>
                  </>
                ) : null}
              </ul>
            )
          })()}
        </Floating>
      ) : null}

      {editor && d ? (
        <PlanningComposer key={editor.shift?.id ?? `${editor.personId}-${editor.day}-${editor.tab ?? 'shift'}-${editor.positionId ?? ''}`} editor={editor} data={d} canEdit={canEdit} canLeave={canLeave} canUnassign={canEdit} initialTab={editor.tab}
          dayLeaves={leavesOn(editor.personId, editor.day)} dayShifts={(editor.personId ? byPerson.get(editor.personId) ?? [] : []).filter((s) => parisYmd(s.starts_at) === editor.day)}
          onClose={() => setEditor(null)} onSaved={(undo) => { if (undo) pushUndo(undo); setEditor(null); load() }}
          onLeaveSaved={() => { setEditor(null); load() }}
          apiCreate={apiCreate} apiUpdate={apiUpdate} apiDelete={async (id) => { await deleteShifts([id]) }} onRepeat={(s) => { setEditor(null); setRepeatFor(s) }} />
      ) : null}
      {repeatFor && d ? <RepeatModal shift={repeatFor} onClose={() => setRepeatFor(null)} onDone={() => { setRepeatFor(null); load() }} /> : null}
      {publish ? <PublishModal publish={publish} fmtSnap={fmtSnap} onClose={() => setPublish(null)} onPublish={doPublish} /> : null}
      {versionsOpen ? <VersionsModal week={week} diff={diff} nameOf={nameOf} fmtSnap={fmtSnap} onClose={() => setVersionsOpen(false)} /> : null}
      {tplOpen && d ? <TemplatesModal week={week} templates={d.templates} onClose={() => setTplOpen(false)} onChanged={load} /> : null}
      {clearOpen && d ? <ClearWeekModal week={week} teams={teams} onClose={() => setClearOpen(false)} onDone={() => { setClearOpen(false); load() }} /> : null}
    </main>
  )
}

/* ═══════════════ Menu ⋯ (actions semaine) ═══════════════ */
function PlanningMenu(p: { canEdit: boolean; onTemplates: () => void; onClear: () => void; onDuplicatePrev: () => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  if (!p.canEdit) return null
  return (
    <>
      <button ref={ref} className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Actions de la semaine" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}><Layers size={15} /></button>
      <Floating open={open} anchorRef={ref} placement="bottom-end" onOutside={() => setOpen(false)} className="lc-menu">
        <ul className="lc-menu__list" role="menu">
          <li className="lc-menu__section">Semaine</li>
          <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setOpen(false); p.onTemplates() }}><LayoutTemplate size={15} /> Modèles…</button></li>
          <li role="none"><button role="menuitem" className="lc-menu__item" onClick={() => { setOpen(false); p.onDuplicatePrev() }}><CopyPlus size={15} /> Copier la semaine précédente</button></li>
          <li role="none"><button role="menuitem" className="lc-menu__item lc-menu__item--danger" onClick={() => { setOpen(false); p.onClear() }}><Trash2 size={15} /> Effacer la semaine…</button></li>
        </ul>
      </Floating>
    </>
  )
}

/* ═══════════════ Panneau AFFICHAGE ═══════════════ */
/** Heure saisie valide : un entier 0..23 — un champ vidé n'est pas « 0 ». */
function hourInput(v: string): number | null {
  const t = v.trim(); if (!/^\d{1,2}$/.test(t)) return null
  const n = Number(t); return n >= 0 && n <= 23 ? n : null
}
function DisplayOptions(p: { density: Density; setDensity: (d: Density) => void; layers: Layers; setLayers: (l: Layers) => void; policy: { display_day_start: number; display_day_end: number } | null; canPolicy: boolean; onPolicySaved: () => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const toast = useToast()
  // Aucune plage en dur (§6) : les champs ne s'initialisent que depuis la politique reçue ; avant, ils sont désactivés.
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  useEffect(() => { if (p.policy) { setStart(String(p.policy.display_day_start)); setEnd(String(p.policy.display_day_end)) } }, [p.policy])
  const loaded = p.policy !== null
  const startH = hourInput(start), endH = hourInput(end)
  const sameBounds = startH !== null && endH !== null && startH === endH
  const canSave = loaded && p.canPolicy && startH !== null && endH !== null && !sameBounds
  const rows: { key: keyof Layers; label: string; desc: string }[] = [
    { key: 'unassigned', label: 'Non assignés', desc: 'Ligne des besoins sans titulaire' },
    { key: 'leaves', label: 'Absences', desc: 'Absences approuvées et demandes en attente' },
    { key: 'availability', label: 'Disponibilités', desc: 'Réponses aux campagnes' },
    { key: 'totals', label: 'Totaux', desc: 'Heures par jour et par salarié' },
    { key: 'alerts', label: 'Alertes', desc: 'Icône légale sur les shifts' },
  ]
  const savePolicy = async () => {
    if (!canSave || startH === null || endH === null) return
    const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/hr/planning/display-policy', { display_day_start: startH, display_day_end: endH })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `Amplitude de la vue Jour : ${startH}h → ${endH}h.`); p.onPolicySaved()
  }
  return (
    <>
      <button ref={ref} className="lc-btn lc-btn--ghost lc-btn--sm" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}><SlidersHorizontal size={14} /> Affichage</button>
      <Floating open={open} anchorRef={ref} placement="bottom-end" onOutside={() => setOpen(false)} className="pl-display" role="dialog" ariaLabel="Options d’affichage">
        <div className="pl-display__section">
          <span className="pl-display__label">Densité</span>
          <SegmentedControl<Density> value={p.density} onChange={p.setDensity} size="sm" options={DENSITIES.map((k) => ({ value: k, label: DENSITY_LABELS[k] }))} />
        </div>
        <div className="pl-display__section">
          <span className="pl-display__label">Afficher</span>
          {rows.map((r) => (
            <div key={r.key} className="pl-display__row">
              <span className="pl-display__text"><strong>{r.label}</strong><em>{r.desc}</em></span>
              <Switch checked={p.layers[r.key]} onChange={(v) => p.setLayers({ ...p.layers, [r.key]: v })} ariaLabel={r.label} />
            </div>
          ))}
        </div>
        <div className="pl-display__section">
          <span className="pl-display__label">Amplitude de la vue Jour</span>
          {!loaded ? <em className="pl-display__hint">Amplitude en cours de chargement…</em> : null}
          <div className="pl-display__hours">
            <input className="lc-input" type="number" min={0} max={23} value={start} disabled={!loaded || !p.canPolicy} onChange={(e) => setStart(e.target.value)} aria-label="Heure de début" aria-invalid={loaded && startH === null} />
            <span>h →</span>
            <input className="lc-input" type="number" min={0} max={23} value={end} disabled={!loaded || !p.canPolicy} onChange={(e) => setEnd(e.target.value)} aria-label="Heure de fin" aria-invalid={loaded && endH === null} />
            <span>h</span>
            {p.canPolicy ? <button className="lc-btn lc-btn--sm" disabled={!canSave} onClick={() => void savePolicy()}><Check size={13} /> Enregistrer</button> : null}
          </div>
          {loaded && sameBounds ? <em className="pl-display__hint">Le début et la fin doivent différer.</em> : null}
          <em className="pl-display__hint">Un shift hors amplitude élargit automatiquement l’affichage — il n’est jamais caché.</em>
        </div>
      </Floating>
    </>
  )
}

/* ═══════════════ SOIRÉE sous le libellé du jour (4.8, §53-66) ═══════════════
   Le planning LIT le domaine Soirée : aucune clé étrangère, aucune fusion. Il
   affiche ce que le serveur a rattaché à la NUIT COMMERCIALE de la colonne —
   une soirée du vendredi soir qui commence à 23 h 30 ou à 01 h du matin
   appartient à la même nuit, et cette nuit est celle du vendredi.

   0 soirée → RIEN (pas de « Aucune soirée » : une ligne vide sur sept colonnes
   serait du bruit) · 1 → son nom · ≥ 2 → « <nom> +N » et un popover premium. */
function SoireeTag({ list }: { list: Soiree[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  if (list.length === 0) return null
  const first = list[0]!
  // UN LIEN EST UN <a>. Le routeur maison l'intercepte pour naviguer sans
  // recharger, mais laisse passer Ctrl/⌘+clic et le clic milieu : un chef de
  // salle peut ouvrir la soirée dans un onglet SANS perdre son planning, et
  // l'URL s'affiche au survol. Un <button> ne rend aucun de ces services.
  if (list.length === 1) {
    const annulee = first.status === 'CANCELLED'
    const label = `${first.name}${annulee ? ' — annulée' : ''}`
    return (
      <Link href={`/soirees/${first.id}`} className={`pl-soiree${annulee ? ' pl-soiree--annulee' : ''}`} title={`Soirée : ${label}`} aria-label={`Soirée : ${label}`}>
        <Disc3 size={12} aria-hidden="true" />
        {/* « annulée » se LIT à l'écran : le cacher dans un title en faisait
            une information réservée à la souris. */}
        <span className="pl-soiree__name">{label}</span>
      </Link>
    )
  }
  const more = list.length - 1
  return (
    <>
      <button ref={ref} type="button" className="pl-soiree" aria-haspopup="menu" aria-expanded={open}
        title={list.map((s) => s.name).join(' · ')} aria-label={`${list.length} soirées cette nuit`}
        onClick={() => setOpen((o) => !o)}>
        <Disc3 size={12} aria-hidden="true" />
        <span className="pl-soiree__name">{first.name}</span>
        <span className="pl-soiree__more">+{more}</span>
      </button>
      <Floating open={open} anchorRef={ref} placement="bottom-start" onOutside={() => setOpen(false)} className="lc-menu pl-soireepop">
        <ul className="lc-menu__list" role="menu" aria-label="Soirées de la nuit">
          <li className="lc-menu__section">{list.length} soirées cette nuit</li>
          {list.map((s) => (
            <li key={s.id} role="none">
              <Link role="menuitem" className="lc-menu__item" href={`/soirees/${s.id}`} onClick={() => setOpen(false)}>
                <Disc3 size={14} aria-hidden="true" />
                <span className="pl-soireepop__name">{s.name}</span>
                {s.status === 'CANCELLED' ? <span className="pl-soireepop__meta">annulée</span> : s.starts_at ? <span className="pl-soireepop__meta">{parisHm(s.starts_at)}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      </Floating>
    </>
  )
}

/* ═══════════════ Chip ═══════════════ */
interface ChipHandlers {
  canEdit: boolean; density: Density; showAlerts: boolean; published: boolean
  verdictsByShift: Map<string, Verdict[]>; diffByShift: Map<string, DiffEntry['kind']>
  onPointerDown: (e: React.PointerEvent, s: Shift) => void; onPointerMove: (e: React.PointerEvent) => void; onPointerUp: (e: React.PointerEvent, s: Shift) => void
  onHandleDown: (e: React.PointerEvent, s: Shift, edge: 'start' | 'end') => void; onHandleMove: (e: React.PointerEvent) => void; onHandleUp: (e: React.PointerEvent, s: Shift) => void
  resizeHint: { id: string; text: string } | null; onOpenEditor: (s: Shift) => void; selectedId: string | null
}
/** Marqueur « indisponible » (vue Postes) : le salarié affecté a une absence ce jour — une absence n'est pas un poste. */
type LeaveMark = { short: string; label: string; pending: boolean }
function leaveMarkOf(ls: LeaveRec[]): LeaveMark | undefined {
  const l = ls.find((x) => x.status === 'APPROVED') ?? ls[0]
  return l ? { short: leaveAbbr(l.type_key, l.type_label), label: l.type_label, pending: l.status === 'REQUESTED' } : undefined
}
function ShiftChip({ s, selected, showWho, whoName, h, leaveMark }: { s: Shift; selected: boolean; showWho?: boolean | undefined; whoName?: string | undefined; h: ChipHandlers; leaveMark?: LeaveMark | undefined }) {
  const tone = shiftTone(s)
  const verdicts = h.verdictsByShift.get(s.id) ?? []
  const worst = verdicts.some((v) => v.severity === 'BLOCK') ? 'block' : verdicts.some((v) => v.severity === 'WARNING') ? 'warn' : null
  // Point « non publié » seulement quand une publication existe : sans elle, tout serait « nouveau ».
  const diffKind = h.published ? h.diffByShift.get(s.id) : undefined
  const time = h.resizeHint?.id === s.id ? h.resizeHint.text : `${parisHm(s.starts_at)}–${parisHm(s.ends_at)}`
  const title = `${s.source === 'UNPLANNED' ? 'SERVICE NON PRÉVU · ' : ''}${s.position_name ?? 'Poste non renseigné'} · ${parisHm(s.starts_at)}–${parisHm(s.ends_at)}${s.location_code ? ` · ${s.location_code}` : ''} · ${fmtDuration(s.paid_minutes)} rémunérées${s.break_minutes ? ` · pause ${s.break_minutes} min ${s.break_paid ? 'payée' : 'non payée'}` : ''}${verdicts[0] ? `\n⚠ ${verdicts[0].message}` : ''}${leaveMark ? `\n${leaveMark.pending ? 'Demande d’absence en attente' : 'Indisponible'} · ${leaveMark.label}` : ''}`
  return (
    // `data-shift` : le clic droit est délégué à la surface (§47), qui retrouve
    // le shift par son identifiant plutôt que par une capture de rendu.
    <div className={`pl-chip pl-chip--${tone}${selected ? ' pl-chip--selected' : ''}${h.selectedId === s.id ? ' pl-chip--inspected' : ''}${!s.person_id ? ' pl-chip--unassigned' : ''}${diffKind ? ' pl-chip--pending' : ''}`}
      data-shift={s.id}
      onPointerDown={(e) => h.onPointerDown(e, s)} onPointerMove={h.onPointerMove} onPointerUp={(e) => h.onPointerUp(e, s)}
      onDoubleClick={(e) => { e.stopPropagation(); h.onOpenEditor(s) }}
      role="button" tabIndex={0} aria-label={title} title={title}
      onKeyDown={(e) => { if (e.key === 'Enter') h.onOpenEditor(s) }}>
      {h.canEdit ? <span className="pl-chip__handle pl-chip__handle--l" onPointerDown={(e) => h.onHandleDown(e, s, 'start')} onPointerMove={h.onHandleMove} onPointerUp={(e) => void h.onHandleUp(e, s)} /> : null}
      <span className="pl-chip__head">
        <span className={`pl-chip__pos${!s.position_id ? ' pl-chip__pos--none' : ''}`}>{s.position_name ?? 'Poste non renseigné'}</span>
        <span className="pl-chip__icons">
          {s.recurrence_group_id ? <Repeat size={10} aria-hidden="true" /> : null}
          {s.source === 'UNPLANNED' ? <span className="pl-chip__nonprevu" title="Service non prévu — né d’un pointage"><Sparkles size={10} aria-hidden="true" /></span> : null}
          {s.break_minutes > 0 && !s.break_paid ? <Coffee size={10} aria-hidden="true" /> : null}
          {h.showAlerts && worst ? <span className={`pl-chip__alert pl-chip__alert--${worst}`}><AlertTriangle size={10} aria-hidden="true" /></span> : null}
          {leaveMark ? <span className={`pl-chip__leave${leaveMark.pending ? ' pl-chip__leave--pending' : ''}`} title={`${leaveMark.pending ? 'Demande d’absence en attente' : 'Indisponible'} · ${leaveMark.label}`}><CalendarOff size={10} aria-hidden="true" /></span> : null}
        </span>
      </span>
      <span className="pl-chip__time">{time}</span>
      <span className="pl-chip__meta">
        {showWho ? <span className="pl-chip__who">{whoName ?? '⟶ à pourvoir'}</span> : null}
        {leaveMark ? <span className={`pl-chip__leavetxt${leaveMark.pending ? ' pl-chip__leavetxt--pending' : ''}`}>{leaveMark.pending ? 'demande' : 'indisponible'} ({leaveMark.short})</span> : null}
        {s.location_code ? <span>{s.location_code}</span> : null}
        <span className="pl-chip__dur">{fmtH(s.paid_minutes)}</span>
      </span>
      {h.canEdit ? <span className="pl-chip__handle pl-chip__handle--r" onPointerDown={(e) => h.onHandleDown(e, s, 'end')} onPointerMove={h.onHandleMove} onPointerUp={(e) => void h.onHandleUp(e, s)} /> : null}
    </div>
  )
}

/* ═══════════════ Carte d'ABSENCE (≠ shift : hachurée, non draggable, clic → inspector) ═══════════════ */
function LeaveCard({ l, day, selected, onOpen }: { l: LeaveRec; day: string; selected: boolean; onOpen: () => void }) {
  const tone = leaveTone(l.color)
  const pending = l.status === 'REQUESTED'
  const when = leaveDayLabel(l, day)
  const title = `${l.type_label} · ${when} — ${pending ? 'demande en attente (non validée)' : 'approuvée'}`
  return (
    <button type="button" className={`pl-leave pl-tone--${tone}${pending ? ' pl-leave--requested' : ''}${selected ? ' pl-leave--inspected' : ''}`}
      title={title} aria-label={title}
      onClick={(e) => { e.stopPropagation(); onOpen() }} onDoubleClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <span className="pl-leave__icon" aria-hidden="true">{pending ? <Hourglass size={12} /> : <CalendarOff size={12} />}</span>
      <span className="pl-leave__body">
        <span className="pl-leave__type"><span className="pl-leave__label">{l.type_label}</span>{pending ? <span className="pl-leave__badge">En attente</span> : null}</span>
        <span className="pl-leave__when">{when}</span>
      </span>
    </button>
  )
}

/* ═══════════════ Vue SEMAINE · EMPLOYÉS ═══════════════ */
function WeekEmployees(p: {
  d: WeekData; week: string; people: PersonMeta[]; byPerson: Map<string, Shift[]>; verdictsByPerson: Map<string, Verdict[]>; dayTotals: number[]; weekTotal: number; todayIdx: number
  canEdit: boolean; selection: Set<string>; dropPreview: { key: string; lines: string[]; bad: boolean; copy: boolean } | null; unassignedShifts: Shift[]; layers: Layers; matchesFilters: (s: Shift) => boolean
  leavesFor: (pid: string | null, day: number) => LeaveRec[]; availFor: (pid: string | null, day: number) => { status: string } | null
  hoveredCell: React.MutableRefObject<{ personId: string | null; day: number } | null>
  handlers: ChipHandlers; setEditor: (e: EditorState) => void
  inspectedLeaveId: string | null; onOpenLeave: (id: string) => void
  soireesOn: (ymd: string) => Soiree[]
}) {
  const { week, people, byPerson, verdictsByPerson, dayTotals, weekTotal, todayIdx, canEdit, selection, dropPreview, unassignedShifts, layers, matchesFilters, leavesFor, availFor, hoveredCell, handlers, setEditor, inspectedLeaveId, onOpenLeave, soireesOn } = p
  const unassignedGroups = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of unassignedShifts) m.set(s.position_name ?? 'Poste non renseigné', (m.get(s.position_name ?? 'Poste non renseigné') ?? 0) + 1)
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [unassignedShifts])
  const cell = (personId: string | null, day: number, shifts: Shift[], rowLabel: string, alt = false) => {
    const key = `${personId ?? UNASSIGNED}:${day}`
    const date = addDays(week, day)
    const prev = dropPreview?.key === key ? dropPreview : null
    const lvs = layers.leaves ? leavesFor(personId, day) : []; const av = layers.availability ? availFor(personId, day) : null
    const open = () => { if (canEdit) setEditor({ personId, day: date }) }
    return (
      // Cellule FOCALISABLE (4.7) : le clic la focalise, Tab l'atteint, Entrée/Espace ouvrent le composer, flèches pour circuler — même geste que le double-clic.
      <div key={key} role="gridcell" className={`pl-cell${alt ? ' pl-row--alt' : ''}${prev ? ` pl-cell--target${prev.bad ? ' pl-cell--target-bad' : ''}` : ''}${day === todayIdx ? ' pl-cell--today' : ''}`}
        data-cell="1" data-person={personId ?? UNASSIGNED} data-day={day} data-date={date}
        tabIndex={0} aria-label={`${rowLabel} — ${fmtDateShort(date)}${canEdit ? ' (Entrée : ajouter)' : ''}`}
        onKeyDown={(e) => cellKeyDown(e, open)}
        onMouseDown={focusCellFromPointer}
        onMouseEnter={() => { hoveredCell.current = { personId, day } }}
        onDoubleClick={open}>
        {lvs.map((l) => <LeaveCard key={l.id} l={l} day={date} selected={l.id === inspectedLeaveId} onOpen={() => onOpenLeave(l.id)} />)}
        {av ? <span className={`pl-avail ${av.status === 'AVAILABLE' ? 'pl-avail--ok' : av.status === 'PARTIAL' ? 'pl-avail--warn' : 'pl-avail--no'}`}>{av.status === 'AVAILABLE' ? '✓ Dispo' : av.status === 'UNAVAILABLE' ? '✕ Indispo' : '⚠ Partiel'}</span> : null}
        {shifts.map((s) => <ShiftChip key={s.id} s={s} selected={selection.has(s.id)} h={handlers} />)}
        {prev ? <span className={`pl-dropprev${prev.bad ? ' pl-dropprev--bad' : ''}`}>{prev.copy ? <span className="pl-dropprev__mode">Copie</span> : null}{prev.lines.map((l, i) => <span key={i}>{l}</span>)}</span> : null}
        {canEdit && shifts.length === 0 && lvs.length === 0 && !prev ? <button className="pl-add" tabIndex={-1} aria-label="Ajouter" onClick={open}><Plus size={16} /></button> : null}
      </div>
    )
  }
  return (
    <div className="pl-gridwrap">
      {/* Grille ARIA : role=grid / row / columnheader / rowheader / gridcell — les rangées sont des boîtes `display: contents` (les cellules restent des items de la grille CSS). */}
      <div className="pl-grid" role="grid" aria-label="Planning de la semaine par salarié" style={{ gridTemplateColumns: `var(--pl-namecol) repeat(7, minmax(var(--pl-colmin), 1fr))` }}>
        <div className="pl-gridrow" role="row">
          <div className="pl-corner" role="columnheader">
            <span className="pl-corner__count">{people.length} salarié{people.length > 1 ? 's' : ''}</span>
            {layers.totals ? <span className="pl-corner__total">{weekTotal > 0 ? `${fmtH(weekTotal)} planifiées` : '—'}</span> : null}
          </div>
          {DAY_LABELS.map((l, i) => (
            <div key={l} role="columnheader" className={`pl-dayhead${i === todayIdx ? ' pl-dayhead--today' : ''}${i >= 5 ? ' pl-dayhead--we' : ''}`}>
              <span className="pl-dayhead__row">
                <span className="pl-dayhead__name">{l} <b>{new Date(`${addDays(week, i)}T12:00:00Z`).getUTCDate()}</b></span>
                {layers.totals ? <span className="pl-dayhead__total">{dayTotals[i]! > 0 ? fmtH(dayTotals[i]!) : ''}</span> : null}
              </span>
              {/* Clé DATÉE : sans elle React réconcilie par position (la clé de
                  colonne est « Lun…Dim »), l'instance survit au changement de
                  semaine et son popover reste ouvert sur d'autres soirées. */}
              <SoireeTag key={addDays(week, i)} list={soireesOn(addDays(week, i))} />
            </div>
          ))}
        </div>
        {layers.unassigned ? (
          <div className="pl-gridrow" role="row">
            <div className="pl-namecell pl-namecell--unassigned" role="rowheader" data-personrow={UNASSIGNED}>
              <span className="pl-namecell__avatar pl-namecell__avatar--una"><Sparkles size={13} aria-hidden="true" /></span>
              <span className="pl-namecell__body">
                <span className="pl-namecell__name">Non assignés{unassignedShifts.length ? <span className="pl-namecell__badge">{unassignedShifts.length}</span> : null}</span>
                <span className="pl-namecell__meta">{unassignedGroups.length ? unassignedGroups.map(([n, c]) => `${n} ×${c}`).join(' · ') : 'Aucun besoin à pourvoir'}</span>
              </span>
            </div>
            {DAY_LABELS.map((_, day) => cell(null, day, unassignedShifts.filter((s) => dayIndex(week, s.starts_at) === day), 'Non assignés'))}
          </div>
        ) : null}
        {people.map((pers, idx) => {
          const shifts = (byPerson.get(pers.person_id) ?? []).filter(matchesFilters)
          const total = shifts.reduce((a, s) => a + s.paid_minutes, 0)
          const pv = verdictsByPerson.get(pers.person_id) ?? []
          const worst = pv.some((v) => v.severity === 'BLOCK') ? 'block' : pv.some((v) => v.severity === 'WARNING') ? 'warn' : null
          const contract = pers.weekly_minutes
          const alt = idx % 2 === 1
          return (
            <div key={pers.person_id} className="pl-gridrow" role="row">
              <div className={`pl-namecell${alt ? ' pl-row--alt' : ''}`} role="rowheader" data-personrow={pers.person_id}>
                <span className="pl-namecell__avatar">{initials(humanName(pers.display_name))}</span>
                <span className="pl-namecell__body">
                  <a href={`/equipe/rh/salaries/${pers.person_id}`} className="pl-namecell__name">{humanName(pers.display_name)}{layers.alerts && worst ? <span className={`pl-flag pl-flag--${worst}`} title={pv[0]?.message}>{worst === 'block' ? <ShieldAlert size={11} /> : <AlertTriangle size={11} />}</span> : null}</a>
                  {layers.totals ? (
                    <span className="pl-namecell__meta">
                      {contract != null ? <>{fmtH(contract)} <span className="pl-namecell__sep">·</span> {total > 0 ? `${fmtH(total)} planifiées` : 'rien de planifié'}{total > 0 && total !== contract ? <span className={total > contract ? ' pl-delta--plus' : ' pl-delta--minus'}> {total > contract ? '+' : ''}{fmtH(total - contract)}</span> : null}</>
                        : <>{pers.employment_kind === 'EXTRA' ? <>Extra <span className="pl-namecell__sep">·</span> </> : null}{total > 0 ? `${fmtH(total)} planifiées` : '—'}</>}
                    </span>
                  ) : null}
                </span>
              </div>
              {DAY_LABELS.map((_, day) => cell(pers.person_id, day, shifts.filter((s) => dayIndex(week, s.starts_at) === day), humanName(pers.display_name), alt))}
            </div>
          )
        })}
        {people.length === 0 ? <div className="pl-gridrow" role="row"><div className="pl-empty" role="gridcell" aria-colspan={8} style={{ gridColumn: '1 / -1' }}>Aucun salarié ne correspond aux filtres.</div></div> : null}
      </div>
    </div>
  )
}

/* ═══════════════ Vue SEMAINE · POSTES (couverture) ═══════════════ */
function WeekPositions(p: { d: WeekData; week: string; todayIdx: number; canEdit: boolean; selection: Set<string>; dropPreview: { key: string; lines: string[]; bad: boolean; copy: boolean } | null; matchesFilters: (s: Shift) => boolean; layers: Layers; handlers: ChipHandlers; setEditor: (e: EditorState) => void; leavesFor: (pid: string | null, day: number) => LeaveRec[]; soireesOn: (ymd: string) => Soiree[] }) {
  const { d, week, todayIdx, canEdit, selection, dropPreview, matchesFilters, layers, handlers, setEditor, leavesFor, soireesOn } = p
  const groups: { id: string | null; name: string; color: string | null }[] = [...d.positions.map((x) => ({ id: x.id as string | null, name: x.name, color: x.color })), { id: null, name: 'Poste non renseigné', color: null }]
  const shiftsByPos = new Map<string, Shift[]>()
  for (const s of d.shifts.filter(matchesFilters)) { const k = s.position_id ?? 'null'; const a = shiftsByPos.get(k) ?? []; a.push(s); shiftsByPos.set(k, a) }
  return (
    <div className="pl-gridwrap">
      <div className="pl-grid" role="grid" aria-label="Couverture de la semaine par poste" style={{ gridTemplateColumns: `var(--pl-namecol) repeat(7, minmax(var(--pl-colmin), 1fr))` }}>
        <div className="pl-gridrow" role="row">
          <div className="pl-corner" role="columnheader"><span className="pl-corner__count">Couverture par poste</span></div>
          {DAY_LABELS.map((l, i) => (
            <div key={l} role="columnheader" className={`pl-dayhead${i === todayIdx ? ' pl-dayhead--today' : ''}${i >= 5 ? ' pl-dayhead--we' : ''}`}>
              <span className="pl-dayhead__row"><span className="pl-dayhead__name">{l} <b>{new Date(`${addDays(week, i)}T12:00:00Z`).getUTCDate()}</b></span></span>
              {/* Clé DATÉE : sans elle React réconcilie par position (la clé de
                  colonne est « Lun…Dim »), l'instance survit au changement de
                  semaine et son popover reste ouvert sur d'autres soirées. */}
              <SoireeTag key={addDays(week, i)} list={soireesOn(addDays(week, i))} />
            </div>
          ))}
        </div>
        {groups.map((g, idx) => {
          const gs = shiftsByPos.get(g.id ?? 'null') ?? []
          if (g.id === null && gs.length === 0) return null
          const toFill = gs.filter((s) => !s.person_id).length
          const tone: PositionTone | 'none' = g.id ? shiftTone({ position_id: g.id, position_color: g.color }) : 'none'
          const rowKey = g.id ?? 'noposition'
          return (
            <div key={rowKey} className="pl-gridrow" role="row">
              <div className={`pl-namecell pl-namecell--pos${idx % 2 === 1 ? ' pl-row--alt' : ''}`} role="rowheader" data-personrow={rowKey}>
                <span className={`pl-namecell__avatar pl-namecell__avatar--tone pl-tone--${tone}`}><Briefcase size={13} aria-hidden="true" /></span>
                <span className="pl-namecell__body">
                  <span className="pl-namecell__name pl-namecell__name--caps">{g.name}</span>
                  <span className="pl-namecell__meta">{gs.length} shift{gs.length > 1 ? 's' : ''}{toFill ? <span className="pl-delta--minus"> · {toFill} à pourvoir</span> : gs.length ? ' · 0 à pourvoir' : ''}</span>
                </span>
              </div>
              {DAY_LABELS.map((_, day) => {
                const cellShifts = gs.filter((s) => dayIndex(week, s.starts_at) === day)
                const prev = dropPreview?.key === `keep:${rowKey}:${day}` ? dropPreview : null
                // Cellule de dépôt (data-cell) qui CONSERVE le salarié (data-keep) : ici un déplacement ne change que le jour.
                const open = () => { if (canEdit) setEditor({ personId: null, day: addDays(week, day), positionId: g.id ?? null }) }
                return (
                  <div key={`${rowKey}-${day}`} role="gridcell" className={`pl-cell${day === todayIdx ? ' pl-cell--today' : ''}${prev ? ` pl-cell--target${prev.bad ? ' pl-cell--target-bad' : ''}` : ''}`}
                    data-cell="1" data-day={day} data-keep="1" data-row={rowKey} data-date={addDays(week, day)} data-ctx="position" data-position={g.id ?? ''}
                    tabIndex={0} aria-label={`${g.name} — ${fmtDateShort(addDays(week, day))}${canEdit ? ' (Entrée : ajouter un besoin)' : ''}`}
                    onKeyDown={(e) => cellKeyDown(e, open)}
                    onMouseDown={focusCellFromPointer}
                    onDoubleClick={open}>
                    {/* Une absence n'est PAS un poste : elle n'a pas de ligne ici — elle marque le shift du salarié affecté. */}
                    {cellShifts.map((s) => <ShiftChip key={s.id} s={s} selected={selection.has(s.id)} showWho whoName={s.person_id ? humanName(d.people.find((pp) => pp.person_id === s.person_id)?.display_name ?? '') : undefined} h={handlers}
                      leaveMark={layers.leaves && s.person_id ? leaveMarkOf(leavesFor(s.person_id, day)) : undefined} />)}
                    {prev ? <span className={`pl-dropprev${prev.bad ? ' pl-dropprev--bad' : ''}`}>{prev.copy ? <span className="pl-dropprev__mode">Copie</span> : null}{prev.lines.map((l, i) => <span key={i}>{l}</span>)}</span> : null}
                    {canEdit && cellShifts.length === 0 && !prev ? <button className="pl-add" tabIndex={-1} aria-label="Ajouter un besoin" onClick={open}><Plus size={16} /></button> : null}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ═══════════════ Vue JOUR ═══════════════ */
function DayView(p: { d: WeekData; refDay: string; mode: Mode; people: PersonMeta[]; canEdit: boolean; matchesFilters: (s: Shift) => boolean; unassignedShifts: Shift[]; layers: Layers; setEditor: (e: EditorState) => void; onInspect: (id: string) => void
  leavesOn: (pid: string | null, ymd: string) => LeaveRec[]; inspectedLeaveId: string | null; onOpenLeave: (id: string) => void; soirees: Soiree[] }) {
  const { d, refDay, mode, people, canEdit, matchesFilters, unassignedShifts, layers, setEditor, onInspect, leavesOn, inspectedLeaveId, onOpenLeave, soirees } = p
  // Journée d'AFFICHAGE (nuit club) : un shift qui commence avant l'heure de bascule
  // appartient à la nuit de la veille, et ses heures se comptent depuis le minuit du
  // jour de référence (dimanche 00:15 vu depuis samedi = 24 h 15) — jamais recalées.
  const rollover = displayRollover(d.policy)
  const displayDay = (iso: string) => parisHourFloat(iso) < rollover ? addDays(parisYmd(iso), -1) : parisYmd(iso)
  const relHour = (iso: string) => parisHourFloat(iso) + (parisYmd(iso) === refDay ? 0 : 24)
  const dayShifts = d.shifts.filter((s) => displayDay(s.starts_at) === refDay && matchesFilters(s))
  const axis = dayAxis(d.policy, dayShifts.map((s) => ({ startHour: relHour(s.starts_at), endHour: relHour(s.ends_at) })))
  // Couverture par créneau : mêmes heures relatives que l'axe (base + index), une fin ≤ début est le lendemain.
  const coverage = axis.hours.map((_, i) => { const H = axis.base + i + 0.5; return dayShifts.filter((s) => { const A = relHour(s.starts_at); const bRaw = relHour(s.ends_at); const B = bRaw <= A ? bRaw + 24 : bRaw; return H >= A && H < B }).length })
  const maxCov = Math.max(1, ...coverage)
  const rows: { id: string | null; name: string; tone?: PositionTone | 'none' }[] = mode === 'positions'
    ? [...d.positions.map((x) => ({ id: x.id as string | null, name: x.name, tone: shiftTone({ position_id: x.id, position_color: x.color }) })), { id: null, name: 'Poste non renseigné', tone: 'none' as const }]
    : [...(layers.unassigned ? [{ id: UNASSIGNED as string | null, name: 'Non assignés' }] : []), ...people.map((pp) => ({ id: pp.person_id as string | null, name: humanName(pp.display_name) }))]
  const rowShifts = (rowId: string | null) => mode === 'positions'
    ? dayShifts.filter((s) => (s.position_id ?? null) === rowId)
    : rowId === UNASSIGNED ? unassignedShifts.filter((s) => displayDay(s.starts_at) === refDay && matchesFilters(s)) : dayShifts.filter((s) => s.person_id === rowId)
  return (
    // pl-day--dense : au-delà de 20 créneaux, une étiquette d'heure sur deux est masquée (13 px minimum, jamais de texte rogné).
    <div className={`pl-day${axis.span > 20 ? ' pl-day--dense' : ''}`}>
      {/* Soirée de la NUIT du jour affiché (§53) : rien du tout quand il n'y en a pas. */}
      {soirees.length > 0 ? (
        <div className="pl-day__night">
          <span className="pl-day__nightlabel">Nuit du {fmtDateShort(refDay)}</span>
          <SoireeTag list={soirees} />
        </div>
      ) : null}
      <div className="pl-day__cov">
        <span className="pl-day__covlabel">Couverture{axis.widened ? <em className="pl-day__widened" title="Un shift dépasse l’amplitude configurée : l’axe a été élargi pour ne rien cacher.">Amplitude élargie</em> : null}</span>
        <div className="pl-day__covbars">
          {axis.hours.map((hh, i) => <div key={i} className="pl-day__covcell"><span className="pl-day__covbar" style={{ height: `${(coverage[i]! / maxCov) * 100}%` }} title={`${coverage[i]} personne(s) à ${hh}h`} />{coverage[i] ? <span className="pl-day__covn">{coverage[i]}</span> : null}<span className="pl-day__covh">{hh}h</span></div>)}
        </div>
      </div>
      <div className="pl-day__rows">
        {rows.map((r) => {
          const rs = rowShifts(r.id === UNASSIGNED ? null : r.id)
          if (mode === 'positions' && r.id === null && rs.length === 0) return null
          // Employés : bandes d'absence DESSOUS (rendues avant), plage horaire si heures, sinon toute l'amplitude — un shift en conflit reste visible par-dessus.
          const rowLeaves = mode === 'employees' && layers.leaves && r.id !== UNASSIGNED ? leavesOn(r.id, refDay) : []
          return (
            <div key={String(r.id)} className="pl-day__row" data-personrow={r.id ?? 'x'}>
              <div className="pl-day__name">{r.tone ? <span className={`pl-posdot pl-tone--${r.tone}`} /> : null}{r.name}</div>
              {/* La piste porte (personne, jour de référence) : c'est une CELLULE pour
                  le menu contextuel (§49). En mode Postes elle est anonyme — même
                  contrat que la vue Postes de la semaine, jamais d'absence. */}
              {/* tabIndex=-1 : focalisable PAR PROGRAMME seulement (Escape rend le focus
                  à la piste d'origine, §37) — jamais dans l'ordre de tabulation, aucun
                  anneau sur un conteneur (DS, focus Live Control). */}
              <div className="pl-day__track" data-cell="1" data-date={refDay} tabIndex={-1}
                {...(mode === 'positions' ? { 'data-ctx': 'position', 'data-position': r.id ?? '' } : { 'data-person': r.id ?? UNASSIGNED })}
                onDoubleClick={() => canEdit && setEditor({ personId: mode === 'positions' ? null : (r.id === UNASSIGNED ? null : r.id), day: refDay, ...(mode === 'positions' ? { positionId: r.id ?? null } : {}) })}>
                {axis.hours.map((_, i) => <span key={i} className="pl-day__grid" style={{ left: `${(i / axis.span) * 100}%` }} />)}
                {rowLeaves.map((l) => {
                  const g = leaveDayGeometry(axis, l, refDay); const pending = l.status === 'REQUESTED'
                  const label = `${l.type_label} · ${leaveDayLabel(l, refDay)}${pending ? ' — demande en attente' : ''}`
                  return (
                    <button key={l.id} type="button" className={`pl-day__leave pl-tone--${leaveTone(l.color)}${pending ? ' pl-leave--requested' : ''}${l.id === inspectedLeaveId ? ' pl-leave--inspected' : ''}`}
                      style={g ? { left: `${g.left}%`, width: `${g.width}%` } : { left: 0, width: '100%' }} title={label} aria-label={label}
                      onClick={() => onOpenLeave(l.id)} onDoubleClick={(e) => e.stopPropagation()}>
                      {pending ? <Hourglass size={12} aria-hidden="true" /> : <CalendarOff size={12} aria-hidden="true" />}<span>{label}</span>
                    </button>
                  )
                })}
                {rs.map((s) => {
                  const g = barGeometry(axis, relHour(s.starts_at), relHour(s.ends_at))
                  const mark = mode === 'positions' && layers.leaves && s.person_id ? leaveMarkOf(leavesOn(s.person_id, parisYmd(s.starts_at))) : undefined
                  return (
                    <button key={s.id} className={`pl-day__bar pl-chip--${shiftTone(s)}${!s.person_id ? ' pl-chip--unassigned' : ''}`} style={{ left: `${g.left}%`, width: `${g.width}%` }}
                      data-shift={s.id}
                      onClick={() => onInspect(s.id)} onDoubleClick={() => setEditor({ shift: s, personId: s.person_id, day: refDay })}
                      title={`${s.position_name ?? 'Poste non renseigné'} · ${parisHm(s.starts_at)}–${parisHm(s.ends_at)}${s.location_code ? ` · ${s.location_code}` : ''}${mark ? `\n${mark.pending ? 'Demande d’absence en attente' : 'Indisponible'} · ${mark.label}` : ''}`}>
                      <span className="pl-day__barpos">{s.position_name ?? 'Poste non renseigné'}</span>
                      <span className="pl-day__bartime">{parisHm(s.starts_at)}–{parisHm(s.ends_at)}{s.location_code ? ` · ${s.location_code}` : ''}{mode === 'positions' && s.person_id ? ` · ${humanName(d.people.find((pp) => pp.person_id === s.person_id)?.display_name ?? '')}` : mode === 'positions' ? ' · à pourvoir' : ''}
                        {mark ? <span className={`pl-chip__leavetxt${mark.pending ? ' pl-chip__leavetxt--pending' : ''}`}> · {mark.pending ? 'demande' : 'indisponible'} ({mark.short})</span> : null}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
        {rows.length === 0 ? <p className="pl-empty">Personne n’est planifié ce jour.</p> : null}
      </div>
    </div>
  )
}

/* ═══════════════ Vue MOIS ═══════════════ */
function MonthView(p: { range: RangeData | null; refDay: string; monthFirst: string; monthDays: number; teamFilter: string; query: string; personFilter: string; positionFilter: string; roomFilter: string; setRefDay: (d: string) => void; setView: (v: View) => void; leaves: LeaveRec[] }) {
  const { range, refDay, monthFirst, monthDays, teamFilter, query, personFilter, positionFilter, roomFilter, setRefDay, setView, leaves } = p
  if (!range) return <div className="pl-skeleton" aria-hidden="true">{Array.from({ length: 6 }, (_, i) => <div key={i} className="pl-skeleton__row" />)}</div>
  const days = Array.from({ length: monthDays }, (_, i) => `${monthFirst.slice(0, 7)}-${String(i + 1).padStart(2, '0')}`)
  const byPerson = new Map<string, Shift[]>()
  for (const s of range.shifts) { if (!s.person_id) continue; if (positionFilter && s.position_id !== positionFilter) continue; if (roomFilter && s.location_id !== roomFilter) continue; const a = byPerson.get(s.person_id) ?? []; a.push(s); byPerson.set(s.person_id, a) }
  const leavesByPerson = new Map<string, LeaveRec[]>()
  for (const l of leaves) { const a = leavesByPerson.get(l.person_id) ?? []; a.push(l); leavesByPerson.set(l.person_id, a) }
  let people = range.people
  if (teamFilter) people = people.filter((pp) => pp.teams.includes(teamFilter))
  if (query.trim()) { const q = query.trim().toLowerCase(); people = people.filter((pp) => pp.display_name.toLowerCase().includes(q)) }
  if (personFilter) people = people.filter((pp) => pp.person_id === personFilter)
  const goDay = (ymd: string) => { setRefDay(ymd); setView('day') }
  const today = todayYmd()
  return (
    <div className="pl-gridwrap">
      {/* 40 px minimum par jour : la pastille « ⧗ RÉC » (sablier + abréviation) tient sans être rognée. */}
      <div className="pl-month" style={{ gridTemplateColumns: `var(--pl-namecol) repeat(${monthDays}, minmax(40px, 1fr))` }}>
        <div className="pl-corner"><span className="pl-corner__count">{people.length} salarié{people.length > 1 ? 's' : ''}</span></div>
        {days.map((ymd) => { const wd = (new Date(`${ymd}T12:00:00Z`).getUTCDay() + 6) % 7; return <div key={ymd} className={`pl-month__dayhead${wd >= 5 ? ' pl-month__dayhead--we' : ''}${ymd === today ? ' pl-month__dayhead--today' : ''}`}><span>{DAY_LABELS[wd]![0]}</span><b>{Number(ymd.slice(8))}</b></div> })}
        {people.map((pp, idx) => {
          const shifts = byPerson.get(pp.person_id) ?? []
          const total = shifts.filter((s) => parisYmd(s.starts_at).slice(0, 7) === refDay.slice(0, 7)).reduce((a, s) => a + s.paid_minutes, 0)
          return [
            <div key={`${pp.person_id}-n`} className={`pl-namecell${idx % 2 === 1 ? ' pl-row--alt' : ''}`} data-personrow={pp.person_id}>
              <span className="pl-namecell__avatar">{initials(humanName(pp.display_name))}</span>
              <span className="pl-namecell__body">
                <a href={`/equipe/rh/salaries/${pp.person_id}`} className="pl-namecell__name">{humanName(pp.display_name)}</a>
                <span className="pl-namecell__meta">{total > 0 ? `${fmtH(total)} ce mois` : '—'}</span>
              </span>
            </div>,
            ...days.map((ymd) => {
              const cs = shifts.filter((s) => parisYmd(s.starts_at) === ymd)
              const ls = (leavesByPerson.get(pp.person_id) ?? []).filter((l) => l.starts_on <= ymd && l.ends_on >= ymd)
              const lead = ls.find((l) => l.status === 'APPROVED') ?? ls[0]
              const leadPending = lead?.status === 'REQUESTED'
              const title = [
                ...ls.map((l) => `${l.type_label} · ${leaveDayLabel(l, ymd)}${l.status === 'REQUESTED' ? ' (demande en attente)' : ''}`),
                ...cs.map((s) => `${s.position_name ?? 'Poste non renseigné'} ${parisHm(s.starts_at)}–${parisHm(s.ends_at)}`),
              ].join('\n')
              // « +N » à 13 px : au-delà de deux shifts, un seul point + le reste — jamais un chiffre rogné dans 36 px.
              const dots = cs.length <= 2 ? cs : cs.slice(0, 1)
              return <button key={`${pp.person_id}-${ymd}`} className={`pl-month__cell${cs.length ? ' pl-month__cell--has' : ''}${lead ? ' pl-month__cell--leave' : ''}${idx % 2 === 1 ? ' pl-row--alt' : ''}`} onClick={() => goDay(ymd)} title={title}>
                {/* Demande en attente : couleur ambre + sablier + abréviation — jamais la couleur seule (DS §7). */}
                {lead ? <span className={`pl-month__leave pl-tone--${leaveTone(lead.color)}${leadPending ? ' pl-leave--requested' : ''}`} role="img" aria-label={`${lead.type_label}${leadPending ? ' — demande en attente' : ''}`}>{leadPending ? <Hourglass size={11} aria-hidden="true" /> : null}{leaveAbbr(lead.type_key, lead.type_label)}</span> : null}
                {dots.map((s) => <span key={s.id} className={`pl-month__dot pl-tone--${shiftTone(s)}`} />)}
                {cs.length > 2 ? <span className="pl-month__more">+{cs.length - 1}</span> : null}
              </button>
            }),
          ]
        })}
      </div>
    </div>
  )
}

/* ═══════════════ SHIFT INSPECTOR (split intégré) ═══════════════ */
function ShiftInspector(p: {
  shift: Shift; d: WeekData; verdicts: Verdict[]; diffKind: DiffEntry['kind'] | null; published: boolean; canEdit: boolean
  onClose: () => void; onEdit: () => void; onCopy: () => void; onRepeat: () => void; onUnassign: () => void; onDelete: () => void; onAssign: (pid: string) => void
  dayLeaves: LeaveRec[]; onOpenLeave: (id: string) => void
}) {
  const s = p.shift
  const tone = shiftTone(s)
  const who = s.person_id ? p.d.people.find((pp) => pp.person_id === s.person_id) : null
  const span = Math.round((new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 60_000)
  const pubLabel = !p.published ? 'Jamais publié (brouillon)' : p.diffKind === 'ADDED' ? 'Nouveau — non publié' : p.diffKind === 'CHANGED' ? 'Modifié depuis la dernière publication' : 'Publié · à jour'
  const srcLabel: Record<string, string> = { MANUAL: 'Créé à la main', TEMPLATE: 'Issu d’un modèle', COPY: 'Copie', REPEAT: 'Occurrence d’une série' }
  return (
    <aside className="pl-inspector" aria-label="Détail du shift">
      <header className={`pl-inspector__head pl-tonebg--${tone}`}>
        <button className="lc-btn lc-btn--ghost lc-btn--icon pl-inspector__back" aria-label="Fermer le détail" onClick={p.onClose}><ArrowLeft size={16} /></button>
        <span className="pl-inspector__kicker"><span className={`pl-posdot pl-tone--${tone}`} />{s.position_name ?? 'Poste non renseigné'}</span>
        <h2 className="pl-inspector__title">{parisHm(s.starts_at)} – {parisHm(s.ends_at)}</h2>
        <p className="pl-inspector__sub">{capFirst(new Date(`${parisYmd(s.starts_at)}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }))}</p>
        <button className="lc-btn lc-btn--ghost lc-btn--icon pl-inspector__close" aria-label="Fermer" onClick={p.onClose}><X size={16} /></button>
      </header>
      <div className="pl-inspector__body">
        <dl className="pl-facts">
          <div><dt><Users size={13} /> Salarié</dt><dd>{who ? <a href={`/equipe/rh/salaries/${who.person_id}`}>{humanName(who.display_name)}</a> : <span className="pl-facts__muted">Non assigné — besoin à pourvoir</span>}</dd></div>
          <div><dt><Clock size={13} /> Durée</dt><dd>{fmtDuration(span)} planifiées · <strong>{fmtDuration(s.paid_minutes)} rémunérées</strong></dd></div>
          <div><dt><Coffee size={13} /> Pause</dt><dd>{s.break_minutes ? `${s.break_minutes} min · ${s.break_paid ? 'payée' : 'non payée'}` : 'Aucune'}</dd></div>
          <div><dt><MapPin size={13} /> ROOM</dt><dd>{s.location_code ? (p.d.locations.find((l) => l.id === s.location_id)?.name ?? s.location_code) : <span className="pl-facts__muted">—</span>}</dd></div>
          {s.function_name ? <div><dt><Briefcase size={13} /> Fonction</dt><dd>{s.function_name}</dd></div> : null}
          {s.notes ? <div><dt><StickyNote size={13} /> Note</dt><dd>{s.notes}</dd></div> : null}
          <div><dt><Send size={13} /> Publication</dt><dd>{pubLabel}</dd></div>
          <div><dt><History size={13} /> Origine</dt><dd>{srcLabel[s.source] ?? s.source}{s.recurrence_group_id ? ' · membre d’une série' : ''}</dd></div>
        </dl>
        {p.verdicts.length ? (
          <section className="pl-inspector__section">
            <h3>Alertes</h3>
            {p.verdicts.map((v, i) => <div key={i} className={`pl-verdict pl-verdict--${v.severity.toLowerCase()}`}><span className="lc-stack"><span>{v.message}</span><span className="rh-assign__meta">{v.source}{v.article ? ` · ${v.article}` : ''}</span></span></div>)}
          </section>
        ) : <section className="pl-inspector__section pl-inspector__ok"><Check size={14} /> Aucune alerte légale sur ce shift.</section>}
        {p.dayLeaves.length ? (
          <section className="pl-inspector__section">
            <h3>Absence ce jour</h3>
            {p.dayLeaves.map((l) => <LeaveCard key={l.id} l={l} day={parisYmd(s.starts_at)} selected={false} onOpen={() => p.onOpenLeave(l.id)} />)}
          </section>
        ) : null}
        {!s.person_id && p.canEdit ? (
          <section className="pl-inspector__section">
            <h3>Assigner à</h3>
            <Combobox value={null} items={p.d.people.map((pp) => ({ value: pp.person_id, label: humanName(pp.display_name) }))} onChange={(v) => { if (v) p.onAssign(v) }} placeholder="Choisir un salarié…" ariaLabel="Assigner" leading={<Users size={14} />} />
          </section>
        ) : null}
      </div>
      {p.canEdit ? (
        <footer className="pl-inspector__actions">
          <button className="lc-btn lc-btn--primary" onClick={p.onEdit}><Settings2 size={14} /> Modifier</button>
          <button className="lc-btn lc-btn--ghost" onClick={p.onCopy}><Copy size={14} /> Copier</button>
          <button className="lc-btn lc-btn--ghost" onClick={p.onRepeat}><Repeat size={14} /> Répéter</button>
          {s.person_id ? <button className="lc-btn lc-btn--ghost" onClick={p.onUnassign}><UserMinus size={14} /> Désassigner</button> : null}
          <button className="lc-btn lc-btn--ghost lc-btn--danger-text" onClick={p.onDelete}><Trash2 size={14} /> Supprimer</button>
        </footer>
      ) : null}
    </aside>
  )
}

/* ═══════════════ LEAVE INSPECTOR (même panneau que le shift, état discriminé) ═══════════════ */
interface LeaveDetailData {
  leave: Record<string, unknown>
  ledger: Array<Record<string, unknown>>
  audit: Array<Record<string, unknown>>
  conflicts: LeaveConflict[]
}
function readLeaveDetail(raw: unknown): LeaveDetailData | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!r['leave'] || typeof r['leave'] !== 'object') return null
  const objs = (k: string) => (Array.isArray(r[k]) ? r[k] as unknown[] : []).filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
  return {
    leave: r['leave'] as Record<string, unknown>,
    ledger: objs('ledger'), audit: objs('audit'),
    conflicts: objs('conflicts').filter((c) => typeof c['shift_id'] === 'string' && typeof c['starts_at'] === 'string' && typeof c['ends_at'] === 'string')
      .map((c) => ({ shift_id: c['shift_id'] as string, starts_at: c['starts_at'] as string, ends_at: c['ends_at'] as string, position_name: typeof c['position_name'] === 'string' ? c['position_name'] : null, location_code: typeof c['location_code'] === 'string' ? c['location_code'] : null })),
  }
}
const LEAVE_STATUS: Record<string, { label: string; tone: 'ok' | 'warning' | 'error' | 'idle' }> = {
  APPROVED: { label: 'Approuvée', tone: 'ok' }, REQUESTED: { label: 'Demande en attente', tone: 'warning' },
  REJECTED: { label: 'Refusée', tone: 'error' }, CANCELLED: { label: 'Annulée', tone: 'idle' },
}
const LEAVE_SOURCE: Record<string, string> = { SELF: 'Demande du salarié', PLANNING: 'Saisie depuis le planning', MANAGER: 'Saisie par un responsable' }
/* Libellés humains (DS §13) : aucune clé technique dans l'inspector — ni le `kind` du ledger, ni le code d'audit, ni la clé d'une permission. */
const LEDGER_KIND: Record<string, string> = { USAGE: 'Consommation', ADJUSTMENT: 'Ajustement', OPENING_BALANCE: 'Solde initial', ACCRUAL: 'Acquisition', LEGAL_ENTITLEMENT: 'Droit légal', EXPIRY: 'Expiration' }
const LEAVE_DOC_PERM_LABEL: string = PERMISSION_META['leave.document.read']?.label ?? 'Voir les justificatifs d’absence'
const auditLabel = (event: unknown): string => (typeof event === 'string' ? AUDIT_ACTIONS[event]?.label : undefined) ?? 'Événement'
function fmtInstant(iso: string): string { return new Date(iso).toLocaleString('fr-FR', { timeZone: PARIS, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }

function LeaveInspector(p: { leave: LeaveRec; d: WeekData; canCancel: boolean; onClose: () => void; onChanged: () => void; onOpenShift: (id: string) => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const l = p.leave
  const [detail, setDetail] = useState<LeaveDetailData | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (l.synthetic) { setFailed('Identifiant absent : le détail ne peut pas être chargé pendant la transition d’API.'); return }
    let alive = true
    // apiGet résout les 403/404/500 avec { error } : on le dit, on n'affiche pas un détail vide comme s'il était complet.
    apiGet<unknown>(`/api/v1/hr/leave/requests/${l.id}`).then((r) => {
      if (!alive) return
      const dd = readLeaveDetail(r)
      if (dd) setDetail(dd)
      else setFailed((r as { error?: string }).error === 'FORBIDDEN' || (r as { error?: string }).error === 'OUT_OF_SCOPE' ? 'Détail hors de votre périmètre.' : 'Détail indisponible.')
    }).catch(() => { if (alive) setFailed('Détail indisponible.') })
    return () => { alive = false }
  }, [l.id, l.synthetic])
  const lv = detail?.leave ?? {}
  const str = (k: string): string | null => typeof lv[k] === 'string' ? lv[k] as string : null
  const status = str('status') ?? l.status
  const st = LEAVE_STATUS[status] ?? { label: status, tone: 'idle' as const }
  const tone = leaveTone(str('color') ?? l.color)
  const who = p.d.people.find((pp) => pp.person_id === l.person_id)
  const whoName = who ? humanName(who.display_name) : str('display_name') ? humanName(str('display_name')!) : '—'
  const single = l.starts_on === l.ends_on
  const hours = l.hours_from !== null && l.hours_to !== null
  const title = hours ? `${minutesToHm(l.hours_from!)} – ${minutesToHm(l.hours_to!)}` : single ? leaveDayLabel(l, l.starts_on) : `${new Date(`${l.starts_on}T12:00:00Z`).getUTCDate()} → ${fmtDateShort(l.ends_on).replace(/^\S+\s/, '')}`
  const sub = single ? capFirst(fmtDateLong(l.starts_on)) : `Du ${fmtDateLong(l.starts_on)} au ${fmtDateLong(l.ends_on)}`
  // Le détail ne sort la note (document_note) et le document qu'avec leave.document.read ou pour le propriétaire ; sinon `document_masked` — on le dit.
  const note = str('document_note')
  const masked = lv['document_masked'] === true
  const documentId = str('document_id')
  const duration = lv['duration'] && typeof lv['duration'] === 'object' ? lv['duration'] as { amount?: unknown; unit?: unknown } : null
  const durationLabel = duration && typeof duration.amount === 'number'
    ? (duration.unit === 'MINUTES' ? fmtDuration(duration.amount) : `${String(duration.amount).replace('.', ',')} jour${duration.amount > 1 ? 's' : ''}`)
    : null
  const cancellable = p.canCancel && !l.synthetic && (status === 'APPROVED' || status === 'REQUESTED')
  // Confirmation = LA primitive du DS (ConfirmActionDialog) : la question en titre, la conséquence en texte,
  // « Conserver » en ghost pré-focalisé, « Annuler l'absence » en --danger et en dernier. Jamais window.confirm.
  const doCancel = async () => {
    const ok = await confirm({
      title: 'Annuler cette absence ?',
      message: status === 'APPROVED'
        ? 'Le compteur sera contre-passé, le planning n’est pas modifié.'
        : 'La demande disparaît du planning ; aucun compteur n’a été mouvementé.',
      details: [
        { label: 'Salarié', value: whoName },
        { label: 'Absence', value: `${str('type_label') ?? l.type_label} · ${single ? capFirst(fmtDateLong(l.starts_on)) : `du ${fmtDateShort(l.starts_on)} au ${fmtDateShort(l.ends_on)}`}` },
      ],
      variant: 'critical', confirmLabel: 'Annuler l’absence', cancelLabel: 'Conserver',
    })
    if (!ok) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/leave/requests/${l.id}/cancel`)
    setBusy(false)
    if (r.status >= 400) { toast('error', r.data.message ?? (r.status === 403 ? 'Annulation hors de votre périmètre.' : 'Annulation refusée.')); return }
    toast('ok', `Absence annulée — ${whoName}, ${fmtDateShort(l.starts_on)}.`)
    p.onChanged(); p.onClose()
  }
  return (
    <aside className="pl-inspector pl-inspector--leave" aria-label="Détail de l’absence">
      <header className={`pl-inspector__head pl-tonebg--${tone}`}>
        <button className="lc-btn lc-btn--ghost lc-btn--icon pl-inspector__back" aria-label="Fermer le détail" onClick={p.onClose}><ArrowLeft size={16} /></button>
        <span className="pl-inspector__kicker"><span className={`pl-posdot pl-tone--${tone}`} />{str('type_label') ?? l.type_label}</span>
        <h2 className="pl-inspector__title pl-inspector__title--leave">{title}</h2>
        <p className="pl-inspector__sub">{sub}</p>
        <span className={`lc-pill lc-pill--${st.tone} pl-inspector__status`}>{status === 'REQUESTED' ? <Hourglass size={12} aria-hidden="true" /> : status === 'APPROVED' ? <Check size={12} aria-hidden="true" /> : <Ban size={12} aria-hidden="true" />} {st.label}</span>
        <button className="lc-btn lc-btn--ghost lc-btn--icon pl-inspector__close" aria-label="Fermer" onClick={p.onClose}><X size={16} /></button>
      </header>
      <div className="pl-inspector__body">
        <dl className="pl-facts">
          <div><dt><Users size={13} /> Salarié</dt><dd><a href={`/equipe/rh/salaries/${l.person_id}`}>{whoName}</a></dd></div>
          <div><dt><CalendarDays size={13} /> Période</dt><dd>{single ? fmtDateShort(l.starts_on) : `${fmtDateShort(l.starts_on)} → ${fmtDateShort(l.ends_on)}`}{!hours ? <> · <span className="pl-facts__muted">{leaveDayLabel(l, l.starts_on)}{!single && (l.start_half || l.end_half) ? ` / ${leaveDayLabel(l, l.ends_on)}` : ''}</span></> : null}</dd></div>
          {hours ? <div><dt><Clock size={13} /> Heures</dt><dd>{minutesToHm(l.hours_from!)} – {minutesToHm(l.hours_to!)}{l.hours_to! > 1440 ? <span className="pl-facts__muted"> · lendemain</span> : null}</dd></div> : null}
          {durationLabel ? <div><dt><Hourglass size={13} /> Durée</dt><dd>{durationLabel}<span className="pl-facts__muted"> · unité du compteur consommé</span></dd></div> : null}
          <div><dt><CalendarClock size={13} /> Statut</dt><dd>{st.label}{status === 'REQUESTED' ? <span className="pl-facts__muted"> — rien n’est validé tant qu’un approbateur n’a pas tranché</span> : null}</dd></div>
          <div><dt><History size={13} /> Origine</dt><dd>{LEAVE_SOURCE[str('source') ?? l.source] ?? (str('source') ?? l.source)}{str('requested_at') ? <span className="pl-facts__muted"> · demandée le {fmtInstant(str('requested_at')!)}{str('requested_by_name') ? ` par ${str('requested_by_name')}` : ''}</span> : null}{str('decided_at') ? <span className="pl-facts__muted"> · décidée le {fmtInstant(str('decided_at')!)}{str('decided_by_name') ? ` par ${str('decided_by_name')}` : ''}</span> : null}{str('recurrence_group_id') ? <span className="pl-facts__muted"> · membre d’une série</span> : null}</dd></div>
          {note ? <div><dt><StickyNote size={13} /> Note</dt><dd>{note}</dd></div>
            : masked && l.comment_visible ? <div><dt><StickyNote size={13} /> Note</dt><dd className="pl-facts__muted">Une note existe — masquée : nécessite le droit « {LEAVE_DOC_PERM_LABEL} ».</dd></div> : null}
          {str('decision_note') && status !== 'REQUESTED' ? <div><dt><FileText size={13} /> Décision</dt><dd>{str('decision_note')}</dd></div> : null}
          <div><dt><Paperclip size={13} /> Justificatif</dt><dd className="pl-facts__muted">{documentId ? 'Document joint — consultation dans Congés & absences.' : masked ? `Masqué — nécessite le droit « ${LEAVE_DOC_PERM_LABEL} » sur ce salarié.` : 'Aucun document joint — dépôt dans le coffre documentaire à venir, pas depuis le planning.'}</dd></div>
        </dl>
        {failed ? <p className="lc-hint"><AlertTriangle size={13} aria-hidden="true" /> {failed}</p> : !detail ? <p className="lc-hint">Chargement du détail…</p> : (
          <>
            <section className="pl-inspector__section">
              <h3>Impact compteur</h3>
              {detail.ledger.length === 0 ? <p className="lc-hint">Aucun mouvement de compteur lié.</p> : (
                <ul className="pl-inspector__list">
                  {detail.ledger.map((e, i) => <li key={typeof e['id'] === 'string' ? e['id'] : i}><strong>{typeof e['amount'] === 'number' ? `${e['amount'] > 0 ? '+' : ''}${String(e['amount']).replace('.', ',')}` : '—'}{typeof e['kind'] === 'string' ? ` · ${LEDGER_KIND[e['kind']] ?? 'Mouvement'}` : ''}</strong><span>{typeof e['reason'] === 'string' ? e['reason'] : ''}</span>{typeof e['created_at'] === 'string' ? <span className="pl-facts__muted">{fmtInstant(e['created_at'])}</span> : null}</li>)}
                </ul>
              )}
            </section>
            <section className="pl-inspector__section">
              <h3>Historique</h3>
              {detail.audit.length === 0 ? <p className="lc-hint">Aucun événement.</p> : (
                <ul className="pl-inspector__list">
                  {detail.audit.map((e, i) => <li key={i}><strong>{auditLabel(e['event'])}</strong><span>{typeof e['message'] === 'string' ? e['message'] : ''}</span><span className="pl-facts__muted">{typeof e['at'] === 'string' ? fmtInstant(e['at']) : ''}{typeof e['actor'] === 'string' ? ` · ${e['actor']}` : ''}</span></li>)}
                </ul>
              )}
            </section>
            <section className="pl-inspector__section">
              <h3>Conflits de planning</h3>
              {detail.conflicts.length === 0 ? <p className="pl-inspector__ok"><Check size={14} /> Aucun shift en conflit.</p> : (
                <ul className="pl-inspector__list">
                  {detail.conflicts.map((c) => <li key={c.shift_id}><button type="button" className="pl-inspector__conflict" onClick={() => p.onOpenShift(c.shift_id)}><strong>{c.position_name ?? 'Poste non renseigné'}</strong><span>{parisHm(c.starts_at)}–{parisHm(c.ends_at)}{c.location_code ? ` · ${c.location_code}` : ''}</span></button></li>)}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
      <footer className="pl-inspector__actions">
        {/* La page Congés & absences n'ouvre pas (encore) une demande par identifiant : le lien mène à la liste, il ne promet pas plus. */}
        <Link href="/equipe/temps/conges" className="lc-btn lc-btn--ghost"><ExternalLink size={14} /> Voir Congés & absences</Link>
        {cancellable ? <button className="lc-btn lc-btn--ghost lc-btn--danger-text" disabled={busy} onClick={() => void doCancel()}><Ban size={14} /> Annuler l’absence</button> : null}
      </footer>
    </aside>
  )
}

/* ═══════════════ COMPOSER UNIQUE : Shift | Absence ═══════════════
   Une seule surface (Modal owning ~600 px, pas de drawer) ouverte depuis la
   cellule. L'onglet Absence n'est rendu QUE si le droit existe — sinon aucun
   onglet, le composer de shift seul (rien de grisé). Un salarié sur son
   propre planning n'a pas leave.create_for_others : il ne voit pas l'onglet. */
function PlanningComposer(p: {
  editor: EditorState; data: WeekData; canEdit: boolean; canLeave: boolean; canUnassign: boolean
  /**
   * Onglet d'ouverture (4.8) — « Ajouter une absence » du menu contextuel ouvre
   * CE composer sur l'onglet Absence, jamais un second formulaire. Défaut
   * inchangé : sans valeur (et sans `editor.tab`), on ouvre sur Shift.
   */
  initialTab?: 'shift' | 'leave' | undefined
  dayLeaves: LeaveRec[]; dayShifts: Shift[]
  onClose: () => void; onSaved: (undo: UndoAction | null) => void; onLeaveSaved: () => void
  apiCreate: (i: Record<string, unknown>) => Promise<{ ok: boolean; shift?: Shift; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }>
  apiUpdate: (id: string, patch: Record<string, unknown>) => Promise<{ ok: boolean; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }>
  apiDelete: (id: string) => Promise<void>; onRepeat: (s: Shift) => void
}) {
  const editing = !!p.editor.shift
  const showTabs = !editing && p.canLeave
  const [tab, setTab] = useState<'shift' | 'leave'>(showTabs && (p.initialTab ?? p.editor.tab) === 'leave' ? 'leave' : 'shift')
  // Aperçu couleur du header : ton du POSTE (shift) ou couleur du TYPE (absence), remonté par le corps actif.
  const [tone, setTone] = useState<PositionTone | 'none'>('none')
  const who = p.editor.personId ? humanName(p.data.people.find((x) => x.person_id === p.editor.personId)?.display_name ?? '') : null
  const title = editing ? 'Modifier le shift' : who ? `Ajouter pour ${who}` : 'Ajouter'
  const kicker = editing ? 'Shift' : tab === 'leave' ? 'Absence' : who ? 'Shift' : 'Besoin non assigné'
  const dateLabel = capFirst(fmtDateLong(editing ? parisYmd(p.editor.shift!.starts_at) : p.editor.day))
  return (
    <Modal open onClose={p.onClose} ariaLabel={`${title} · ${dateLabel}`} width={600} owning className="pl-composer-modal">
      <div className="pl-composer">
        <header className={`pl-composer__head pl-tonebg--${tone}`}>
          <div className="pl-composer__heading">
            <span className="pl-composer__kicker">{tab === 'leave' ? <CalendarOff size={13} aria-hidden="true" /> : <Briefcase size={13} aria-hidden="true" />} {kicker}</span>
            <h3 className="pl-composer__title">{title}</h3>
            <p className="pl-composer__date">{dateLabel}</p>
          </div>
          {showTabs ? (
            <SegmentedControl<'shift' | 'leave'> value={tab} onChange={setTab} ariaLabel="Type d’entrée"
              options={[{ value: 'shift', label: 'Shift', icon: <Briefcase size={14} /> }, { value: 'leave', label: 'Absence', icon: <CalendarOff size={14} /> }]} />
          ) : null}
        </header>
        {tab === 'leave' && showTabs
          ? <LeaveComposer personId={p.editor.personId} day={p.editor.day} data={p.data} dayShifts={p.dayShifts} canUnassign={p.canUnassign} onTone={setTone} onClose={p.onClose} onSaved={p.onLeaveSaved} />
          : <ShiftComposer editor={p.editor} data={p.data} canEdit={p.canEdit} dayLeaves={p.dayLeaves} onTone={setTone} onClose={p.onClose} onSaved={p.onSaved} apiCreate={p.apiCreate} apiUpdate={p.apiUpdate} apiDelete={p.apiDelete} onRepeat={p.onRepeat} />}
      </div>
    </Modal>
  )
}

/* ═══════════════ Onglet SHIFT (composer 4.6, primitives du DS) ═══════════════ */
function ShiftComposer({ editor, data, canEdit, dayLeaves, onTone, onClose, onSaved, apiCreate, apiUpdate, apiDelete, onRepeat }: {
  editor: EditorState; data: WeekData; canEdit: boolean; dayLeaves: LeaveRec[]; onTone: (t: PositionTone | 'none') => void
  onClose: () => void; onSaved: (undo: UndoAction | null) => void
  apiCreate: (i: Record<string, unknown>) => Promise<{ ok: boolean; shift?: Shift; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }>
  apiUpdate: (id: string, p: Record<string, unknown>) => Promise<{ ok: boolean; message?: string; warnings?: Verdict[]; blocks?: Verdict[] }>
  apiDelete: (id: string) => Promise<void>; onRepeat: (s: Shift) => void
}) {
  const s = editor.shift
  const toast = useToast()
  const defaultPos = editor.personId ? (data.people.find((p) => p.person_id === editor.personId)?.position ?? null) : null
  // Le poste vient d'abord de la LIGNE cliquée en vue Postes (le menu l'a
  // nommée dans son en-tête : le formulaire doit tenir cette promesse), sinon
  // du poste habituel du salarié.
  const defaultPosId = editor.positionId ?? (defaultPos ? (data.positions.find((p) => p.name === defaultPos)?.id ?? null) : null)
  const scopeTeams = data.scope?.teams ?? []
  const [form, setForm] = useState({
    person: (s ? s.person_id : editor.personId) as string | null,
    day: editor.day,
    start: s ? parisHm(s.starts_at) : '23:00',
    end: s ? parisHm(s.ends_at) : '06:00',
    endNextDay: s ? parisYmd(s.ends_at) !== parisYmd(s.starts_at) : true,
    breakMin: s?.break_minutes ?? 0,
    breakPaid: s ? s.break_paid : data.policy.default_break_paid,
    event: s?.event_id ?? '',
    position: (s?.position_id ?? defaultPosId) as string | null,
    fn: s?.function_id ?? '',
    loc: s?.location_id ?? '',
    notes: s?.notes ?? '',
    // Équipe cible d'un besoin non assigné : posée d'office quand l'acteur ne gère qu'une équipe.
    team: (s?.team_id ?? (data.scope?.kind === 'TEAM' && scopeTeams.length === 1 ? scopeTeams[0]!.id : '')) as string,
  })
  const [warnings, setWarnings] = useState<Verdict[]>([])
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { onTone(shiftTone({ position_id: form.position, position_color: data.positions.find((p) => p.id === form.position)?.color ?? null })) }, [form.position]) // eslint-disable-line react-hooks/exhaustive-deps
  const spanMin = (() => { const st = new Date(parisLocalToIso(form.day, form.start)).getTime(); const en = new Date(parisLocalToIso(form.endNextDay ? addDays(form.day, 1) : form.day, form.end)).getTime(); return Math.max(0, Math.round((en - st) / 60_000)) })()
  // La règle du temps rémunéré n'est PAS réécrite ici : elle vit dans
  // @gestion/shared/duration, partagée avec le cœur métier. Deux écritures
  // d'une même règle finissent toujours par diverger.
  const paidMin = paidMinutesFromSpan(spanMin, form.breakMin, form.breakPaid)
  // Équipe cible d'un besoin non assigné : proposée dès qu'il existe des équipes (scope ALL = toutes les équipes actives,
  // optionnelle) ; OBLIGATOIRE pour un périmètre TEAM qui gère plusieurs équipes (sinon le besoin serait invisible à tous).
  const showTeam = form.person === null && scopeTeams.length > 0
  const teamRequired = form.person === null && data.scope?.kind === 'TEAM' && scopeTeams.length > 1

  const save = async () => {
    if (teamRequired && !form.team) { setError('Choisissez l’équipe cible de ce besoin non assigné.'); return }
    const startsAt = parisLocalToIso(form.day, form.start)
    const endsAt = parisLocalToIso(form.endNextDay ? addDays(form.day, 1) : form.day, form.end)
    const body = { person_id: form.person, starts_at: startsAt, ends_at: endsAt, break_minutes: form.breakMin, break_paid: form.breakPaid, position_id: form.position || null, function_id: form.fn || null, location_id: form.loc || null, event_id: form.event || null, notes: form.notes || null, ...(showTeam ? { team_id: form.team || null } : {}) }
    if (s) {
      const before = { person_id: s.person_id, starts_at: s.starts_at, ends_at: s.ends_at, break_minutes: s.break_minutes, break_paid: s.break_paid, position_id: s.position_id, function_id: s.function_id, location_id: s.location_id, event_id: s.event_id, notes: s.notes }
      const r = await apiUpdate(s.id, body); if (!r.ok) { setError(r.message ?? 'Refusé.'); return }
      setWarnings(r.warnings ?? []); onSaved({ label: 'Shift modifié', run: async () => { await apiUpdate(s.id, before) } })
    } else {
      const r = await apiCreate(body); if (!r.ok) { setError(r.message ?? 'Refusé.'); return }
      const created = r.shift
      onSaved(created ? { label: 'Shift créé', run: async () => { await apiDelete(created.id) } } : null)
      if (r.warnings && r.warnings.length > 0) toast('ok', `Créé — à noter : ${r.warnings[0]!.message}`)
    }
  }
  const posItems: ComboItem[] = data.positions.map((p) => ({ value: p.id, label: p.name, dot: `lc-dot--${shiftTone({ position_id: p.id, position_color: p.color })}` }))
  const quick = data.positions.slice(0, 6)
  const approvedLeave = dayLeaves.find((l) => l.status === 'APPROVED') ?? null
  return (
    <>
      <div className="org-editor pl-composer__body" onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && canEdit) { e.preventDefault(); void save() } }}>
        <div className="pl-composer__quick" role="group" aria-label="Postes — accès rapide">
          {quick.map((pp) => { const t = shiftTone({ position_id: pp.id, position_color: pp.color }); return (
            <button key={pp.id} type="button" className={`pl-quick pl-tonechip--${t}${form.position === pp.id ? ' pl-quick--on' : ''}`} disabled={!canEdit} onClick={() => setForm({ ...form, position: pp.id })}><span className={`pl-posdot pl-tone--${t}`} />{pp.name}</button>
          ) })}
        </div>
        <Field label="Poste">
          <Combobox value={form.position} items={posItems} onChange={(v) => setForm({ ...form, position: v })} placeholder="Choisir un poste…" clearable disabled={!canEdit} leading={<Briefcase size={14} />} />
        </Field>
        <div className="pl-composer__times">
          <Field label="Jour">{canEdit ? <DatePicker value={form.day} onPick={(v) => setForm({ ...form, day: v })} label={fmtDateShort(form.day)} ariaLabel="Jour du shift" /> : <span className="lc-input pl-composer__static">{fmtDateShort(form.day)}</span>}</Field>
          <Field label="Début"><input className="lc-input" type="time" value={form.start} disabled={!canEdit} onChange={(e) => setForm({ ...form, start: e.target.value })} /></Field>
          <Field label="Fin"><input className="lc-input" type="time" value={form.end} disabled={!canEdit} onChange={(e) => setForm({ ...form, end: e.target.value })} /></Field>
        </div>
        <Checkbox checked={form.endNextDay} onChange={(v) => setForm({ ...form, endNextDay: v })} disabled={!canEdit} label="La fin est le lendemain (nuit)" />
        <div className="pl-composer__break">
          <Field label="Pause">{canEdit ? <NumberInput value={form.breakMin} onChange={(raw) => setForm({ ...form, breakMin: Math.max(0, Math.min(480, Math.round(Number(raw) || 0))) })} unit="min" min={0} max={480} step={5} steppers width={168} ariaLabel="Pause en minutes" /> : <span className="lc-input pl-composer__static">{form.breakMin} min</span>}</Field>
          <Checkbox checked={form.breakPaid} onChange={(v) => setForm({ ...form, breakPaid: v })} disabled={!canEdit || form.breakMin === 0} label="Pause rémunérée" />
          <div className="pl-composer__dur"><span>Planifiées <strong>{fmtDuration(spanMin)}</strong></span><span className="pl-composer__paid">Rémunérées <strong>{fmtDuration(paidMin)}</strong></span></div>
        </div>
        <p className="pl-composer__aide">
          Le salarié peut fractionner cette pause en plusieurs interruptions. Toutes les pauses
          du shift suivent le même régime. Cette durée est une <strong>prévision</strong> : ce qui
          est retranché du temps rémunéré, c’est la pause réellement pointée.
        </p>
        <div className="pl-composer__times pl-composer__times--2">
          <Field label="Salarié"><Combobox value={form.person} items={data.people.map((p) => ({ value: p.person_id, label: humanName(p.display_name) }))} onChange={(v) => setForm({ ...form, person: v })} placeholder="Non assigné" clearable disabled={!canEdit} leading={<Users size={14} />} /></Field>
          <Field label="ROOM / Lieu"><Combobox value={form.loc || null} items={data.locations.map((l) => ({ value: l.id, label: l.name, hint: l.code }))} onChange={(v) => setForm({ ...form, loc: v ?? '' })} placeholder="—" clearable disabled={!canEdit} leading={<MapPin size={14} />} /></Field>
        </div>
        {(data.soirees ?? []).length > 0 ? (
          <Field label="Soirée (optionnel)"
            helper="Rattachement EXPLICITE : Gestion ne déduit jamais une soirée d’une simple coïncidence de date ou de salle. Un service de semaine n’en a aucune.">
            <Combobox value={form.event || null}
              items={(data.soirees ?? []).filter((e) => e.status !== 'CANCELLED').map((e) => ({ value: e.id, label: e.name, hint: e.night }))}
              onChange={(v) => setForm({ ...form, event: v ?? '' })}
              placeholder="Sans rattachement" clearable disabled={!canEdit} leading={<Disc3 size={14} />} />
          </Field>
        ) : null}
        {showTeam ? (
          <Field label={teamRequired ? 'Équipe cible (obligatoire)' : 'Équipe cible (optionnel)'} helper="Un besoin non assigné n’appartient à personne : seule l’équipe cible le rend visible à ses responsables.">
            <Combobox value={form.team || null} items={scopeTeams.map((t) => ({ value: t.id, label: t.name }))} onChange={(v) => setForm({ ...form, team: v ?? '' })} placeholder="Choisir une équipe…" clearable={!teamRequired} disabled={!canEdit} leading={<Users size={14} />} />
          </Field>
        ) : null}
        {approvedLeave ? <div className="pl-composer__info pl-composer__info--warn"><CalendarOff size={13} aria-hidden="true" /> Absence approuvée ce jour : {approvedLeave.type_label} · {leaveDayLabel(approvedLeave, form.day)} — le serveur signalera le conflit.</div>
          : dayLeaves[0] ? <div className="pl-composer__info"><Hourglass size={13} aria-hidden="true" /> Demande d’absence en attente ce jour : {dayLeaves[0].type_label}.</div> : null}
        <details className="pl-composer__more">
          <summary>Détails supplémentaires</summary>
          <Field label="Fonction (optionnel)"><Combobox value={form.fn || null} items={data.functions.map((f) => ({ value: f.id, label: f.name }))} onChange={(v) => setForm({ ...form, fn: v ?? '' })} placeholder="—" clearable disabled={!canEdit} /></Field>
          <Field label="Note (optionnel)"><TextInput value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} disabled={!canEdit} /></Field>
        </details>
        {error ? <div className="pl-verdict pl-verdict--block">{error}</div> : null}
        {warnings.map((w, i) => <div key={i} className="pl-verdict pl-verdict--warning">{w.message} <span className="rh-assign__meta">({w.source}{w.article ? ` · ${w.article}` : ''})</span></div>)}
      </div>
      <footer className="pl-composer__foot">
        {s && canEdit ? <button className="lc-btn lc-btn--ghost pl-composer__lead" onClick={() => onRepeat(s)}><Repeat size={14} /> Répéter</button> : <span className="pl-composer__tip pl-composer__lead">Entrée pour enregistrer</span>}
        {s && canEdit ? <button className="lc-btn lc-btn--ghost lc-btn--danger-text" onClick={() => { void apiDelete(s.id); onClose() }}><Trash2 size={14} /> Supprimer</button> : null}
        <button className="lc-btn lc-btn--ghost" onClick={onClose}>Fermer</button>
        {canEdit ? <button className="lc-btn lc-btn--primary" onClick={() => void save()}>{s ? 'Enregistrer' : 'Créer le shift'}</button> : null}
      </footer>
    </>
  )
}

/* ═══════════════ Onglet ABSENCE (source PLANNING) ═══════════════ */
const DURATIONS: { value: LeaveDraft['duration']; label: string }[] = [{ value: 'FULL', label: 'Journée complète' }, { value: 'HALF', label: 'Demi-journée' }, { value: 'HOURS', label: 'Personnalisée (heures)' }]
function LeaveComposer(p: { personId: string | null; day: string; data: WeekData; dayShifts: Shift[]; canUnassign: boolean; onTone: (t: PositionTone | 'none') => void; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [types, setTypes] = useState<LeaveTypeRec[] | null>(null)
  const [typesFailed, setTypesFailed] = useState(false)
  useEffect(() => {
    let alive = true
    apiGet<unknown>('/api/v1/hr/leave/types?for=MANAGER').then((r) => { if (!alive) return; const t = readLeaveTypes(r); if (t) setTypes(t); else setTypesFailed(true) }).catch(() => { if (alive) setTypesFailed(true) })
    return () => { alive = false }
  }, [])
  const [person, setPerson] = useState<string | null>(p.personId)
  const [typeId, setTypeId] = useState<string | null>(null)
  const [duration, setDuration] = useState<LeaveDraft['duration']>('FULL')
  const [half, setHalf] = useState<'AM' | 'PM'>('AM')
  const [hFrom, setHFrom] = useState('23:00'); const [hTo, setHTo] = useState('06:00'); const [hNext, setHNext] = useState(true)
  const [from, setFrom] = useState(p.day); const [to, setTo] = useState(p.day)
  const [startHalf, setStartHalf] = useState(false); const [endHalf, setEndHalf] = useState(false)
  const [comment, setComment] = useState('')
  const [repeatDays, setRepeatDays] = useState<number[]>([]); const [repeatUntil, setRepeatUntil] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<LeaveConflict[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const type = types?.find((t) => t.id === typeId) ?? null
  const tone: PositionTone = type ? leaveTone(type.color) : 'violet'
  useEffect(() => { p.onTone(type ? leaveTone(type.color) : 'none') }, [type]) // eslint-disable-line react-hooks/exhaustive-deps
  const multi = from !== to
  // Heures personnalisées : une seule journée (règle serveur) ET un type qui les autorise.
  const hoursAllowed = !multi && (type ? type.allow_hours : true)
  useEffect(() => { if (!hoursAllowed && duration === 'HOURS') setDuration('FULL') }, [hoursAllowed, duration])
  const first = p.dayShifts[0]
  const typeItems: ComboItem[] = (types ?? []).map((t) => ({ value: t.id, label: t.label, dot: `lc-dot--${leaveTone(t.color)}`, ...(t.document_policy === 'REQUIRED' ? { hint: 'justificatif' } : {}) }))
  const toggleDay = (n: number) => setRepeatDays((w) => w.includes(n) ? w.filter((x) => x !== n) : [...w, n])

  const submit = async (conflictAction?: 'KEEP' | 'UNASSIGN') => {
    const built = leaveBody({ person_id: person ?? '', type_id: typeId ?? '', from, to, duration, half, hoursFrom: hFrom, hoursTo: hTo, hoursNextDay: hNext, startHalf, endHalf, comment, repeatWeekdays: repeatDays, repeatUntil })
    if (!built.ok) { setError(built.error); return }
    const body = built.body
    if (conflictAction) body['conflict_action'] = conflictAction
    setBusy(true); setError(null)
    const r = await adminMutate<{ error?: string; message?: string; ok?: boolean; ids?: string[]; status?: string; conflicts?: LeaveConflict[]; unassigned_shift_ids?: string[] }>('POST', '/api/v1/hr/leave/requests', body)
    setBusy(false)
    // 409 CONFLICTS : le serveur a trouvé des shifts — on redemande avec la décision, jamais un window.confirm.
    if (r.status === 409 && r.data.error === 'CONFLICTS') { setConflicts(Array.isArray(r.data.conflicts) ? r.data.conflicts : []); return }
    if (r.status >= 400 || r.data.ok === false) { setError(r.data.message ?? (r.status === 403 ? 'Ce salarié est hors de votre périmètre.' : 'Refusé.')); return }
    setConflicts(null)
    const approved = r.data.status === 'APPROVED'
    const n = Array.isArray(r.data.ids) ? r.data.ids.length : 1
    const un = Array.isArray(r.data.unassigned_shift_ids) ? r.data.unassigned_shift_ids.length : 0
    toast('ok', `${approved ? 'Absence enregistrée (approuvée)' : 'Demande d’absence créée — à approuver'}${n > 1 ? ` · ${n} occurrences` : ''}${un ? ` · ${un} shift${un > 1 ? 's' : ''} placé${un > 1 ? 's' : ''} en non assignés` : ''}.`)
    p.onSaved()
  }
  return (
    <>
      <div className="org-editor pl-composer__body" onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); void submit() } }}>
        {p.personId === null ? (
          <Field label="Salarié"><Combobox value={person} items={p.data.people.map((x) => ({ value: x.person_id, label: humanName(x.display_name) }))} onChange={setPerson} placeholder="Choisir un salarié…" ariaLabel="Salarié" leading={<Users size={14} />} /></Field>
        ) : null}
        <Field label="Type d’absence" error={typesFailed ? 'Types d’absence indisponibles (droit ou service) — impossible d’enregistrer.' : null}>
          <Combobox value={typeId} items={typeItems} onChange={setTypeId} placeholder={types ? 'Choisir un type…' : 'Chargement des types…'} ariaLabel="Type d’absence" disabled={!types} leading={<CalendarOff size={14} />} className="pl-composer__typecombo" />
        </Field>
        {type && type.document_policy !== 'NONE' ? (
          <div className="pl-docs"><Paperclip size={14} aria-hidden="true" /><span>Justificatif <strong>{type.document_policy === 'REQUIRED' ? 'requis' : 'possible'}</strong> pour « {type.label} » — dépôt dans le coffre documentaire à venir, pas depuis le planning.</span></div>
        ) : null}
        <Field label="Durée">
          {/* Une durée indisponible est ABSENTE, pas grisée (DS §8) — le hint dessous dit pourquoi. */}
          <div className="pl-chipset" role="radiogroup" aria-label="Durée">
            {DURATIONS.filter((o) => o.value !== 'HOURS' || hoursAllowed).map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={duration === o.value}
                className={`pl-quick pl-tonechip--${tone}${duration === o.value ? ' pl-quick--on' : ''}`} onClick={() => setDuration(o.value)}>{o.label}</button>
            ))}
          </div>
          {!hoursAllowed && !multi && type ? <span className="lc-hint">« {type.label} » ne se saisit pas en heures.</span> : null}
        </Field>
        {duration === 'FULL' && !multi ? (
          first ? <div className="pl-composer__info"><Clock size={13} aria-hidden="true" /> Shift {parisHm(first.starts_at)}–{parisHm(first.ends_at)}{first.position_name ? ` · ${first.position_name}` : ''}{first.location_code ? ` · ${first.location_code}` : ''} — l’absence couvre la journée métier.</div>
            : <div className="pl-composer__info"><Info size={13} aria-hidden="true" /> Journée métier — aucun shift planifié ce jour.</div>
        ) : null}
        {duration === 'HALF' && !multi ? (
          <Field label="Demi-journée"><SegmentedControl<'AM' | 'PM'> value={half} onChange={setHalf} ariaLabel="Demi-journée" options={[{ value: 'AM', label: 'Matin' }, { value: 'PM', label: 'Après-midi' }]} /></Field>
        ) : null}
        {duration === 'HOURS' && hoursAllowed ? (
          <>
            <div className="pl-composer__times pl-composer__times--2">
              <Field label="Début"><input className="lc-input" type="time" value={hFrom} onChange={(e) => setHFrom(e.target.value)} aria-label="Heure de début" /></Field>
              <Field label="Fin"><input className="lc-input" type="time" value={hTo} onChange={(e) => setHTo(e.target.value)} aria-label="Heure de fin" /></Field>
            </div>
            <Checkbox checked={hNext} onChange={setHNext} label="La fin est le lendemain (nuit)" />
          </>
        ) : null}
        <details className="pl-composer__more" open={multi ? true : undefined}>
          <summary>Période — plusieurs jours</summary>
          <div className="pl-composer__times pl-composer__times--2">
            <Field label="Du"><DatePicker value={from} onPick={(v) => { setFrom(v); if (to < v) setTo(v) }} label={fmtDateShort(from)} highlightFrom={from} highlightTo={to} ariaLabel="Premier jour" /></Field>
            <Field label="Au"><DatePicker value={to} onPick={(v) => { setTo(v); if (v < from) setFrom(v) }} label={fmtDateShort(to)} highlightFrom={from} highlightTo={to} ariaLabel="Dernier jour" /></Field>
          </div>
          {multi ? (
            <>
              <Checkbox checked={startHalf} onChange={setStartHalf} label="Premier jour : après-midi seulement" />
              <Checkbox checked={endHalf} onChange={setEndHalf} label="Dernier jour : matin seulement" />
              <span className="lc-hint">Sur plusieurs jours, la durée se compte en journées (demi-journées aux bornes), jamais en heures.</span>
            </>
          ) : null}
        </details>
        <Field label="Note (optionnel)"><TextInput value={comment} onChange={setComment} placeholder="Motif, précision…" maxLength={300} /></Field>
        <details className="pl-composer__more">
          <summary>Répéter{repeatDays.length ? ` · ${repeatDays.length} jour${repeatDays.length > 1 ? 's' : ''}/semaine` : ''}</summary>
          <Field label="Jours de la semaine"><div className="pl-weekdaypick">{DAY_LABELS.map((l, i) => <button key={i} type="button" aria-pressed={repeatDays.includes(i + 1)} className={`pl-wd${repeatDays.includes(i + 1) ? ' pl-wd--on' : ''}`} onClick={() => toggleDay(i + 1)}>{l}</button>)}</div></Field>
          {repeatDays.length ? <Field label="Jusqu’au"><DatePicker value={repeatUntil ?? to} onPick={setRepeatUntil} label={repeatUntil ? fmtDateShort(repeatUntil) : 'Choisir une date…'} ariaLabel="Répéter jusqu’au" /></Field> : null}
          <span className="lc-hint">Une absence par occurrence (même série), chacune auditée.</span>
        </details>
        {conflicts ? (
          <div className="pl-conflict" role="alert">
            <span className="pl-conflict__title"><AlertTriangle size={14} aria-hidden="true" /> Conflit de planning</span>
            <span>Ce salarié a déjà : {conflicts.length === 0 ? 'un shift sur cette période' : conflicts.map((c) => `${c.position_name ?? 'Poste non renseigné'} ${parisHm(c.starts_at)}–${parisHm(c.ends_at)}${c.location_code ? ` · ${c.location_code}` : ''}`).join(' ; ')}.</span>
            <span className="pl-conflict__actions">
              <button className="lc-btn lc-btn--sm" disabled={busy} onClick={() => void submit('KEEP')}>Conserver le shift en conflit</button>
              {p.canUnassign ? <button className="lc-btn lc-btn--sm lc-btn--ghost" disabled={busy} onClick={() => void submit('UNASSIGN')}><UserMinus size={13} /> Placer le shift en non assigné</button> : null}
            </span>
          </div>
        ) : null}
        {error ? <div className="pl-verdict pl-verdict--block">{error}</div> : null}
      </div>
      <footer className="pl-composer__foot">
        <span className="pl-composer__tip pl-composer__lead">Entrée pour enregistrer</span>
        <button className="lc-btn lc-btn--ghost" onClick={p.onClose}>Fermer</button>
        <button className="lc-btn lc-btn--primary" disabled={busy || !types} onClick={() => void submit()}><CalendarOff size={14} /> Enregistrer l’absence</button>
      </footer>
    </>
  )
}

/* ═══════════════ Répéter ═══════════════ */
function RepeatModal({ shift, onClose, onDone }: { shift: Shift; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const baseWd = (new Date(`${parisYmd(shift.starts_at)}T12:00:00Z`).getUTCDay() + 6) % 7
  const [weekdays, setWeekdays] = useState<number[]>([baseWd])
  const [count, setCount] = useState(4)
  const toggle = (dd: number) => setWeekdays((w) => w.includes(dd) ? w.filter((x) => x !== dd) : [...w, dd])
  const run = async () => {
    const r = await adminMutate<{ error?: string; message?: string; created?: number; skipped?: { date: string; reason: string }[] }>('POST', `/api/v1/hr/planning/shifts/${shift.id}/repeat`, { weekdays, count })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `${r.data.created ?? 0} occurrence${(r.data.created ?? 0) > 1 ? 's' : ''} créée${(r.data.created ?? 0) > 1 ? 's' : ''}${r.data.skipped && r.data.skipped.length ? ` · ${r.data.skipped.length} sautée(s)` : ''}.`)
    onDone()
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Répéter le shift" width={460} owning>
      <div className="org-editor">
        <h3>Répéter ce shift</h3>
        <p className="lc-hint">{shift.position_name ?? 'Poste non renseigné'} · {parisHm(shift.starts_at)}–{parisHm(shift.ends_at)}</p>
        <Field label="Jours de la semaine"><div className="pl-weekdaypick">{DAY_LABELS.map((l, i) => <button key={i} type="button" className={`pl-wd${weekdays.includes(i) ? ' pl-wd--on' : ''}`} onClick={() => toggle(i)}>{l}</button>)}</div></Field>
        <Field label="Nombre d’occurrences"><input className="lc-input" type="number" min={1} max={60} value={count} onChange={(e) => setCount(Math.min(60, Math.max(1, Number(e.target.value))))} /></Field>
        <p className="lc-hint">Les occurrences qui chevauchent un shift existant, ou qu’une règle légale bloque, sont automatiquement sautées.</p>
        <div className="org-editor__foot">
          <button className="lc-btn lc-btn--ghost" onClick={onClose}>Annuler</button>
          <button className="lc-btn lc-btn--primary" disabled={weekdays.length === 0} onClick={() => void run()}>Créer la série</button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════ Publier ═══════════════ */
function PublishModal({ publish, fmtSnap, onClose, onPublish }: { publish: PublishPreview; fmtSnap: (s: SnapRow | null) => string; onClose: () => void; onPublish: () => void }) {
  return (
    <Modal open onClose={onClose} ariaLabel="Publier le planning" width={640} owning>
      <div className="org-editor">
        <h3>Publier le planning — v{publish.version_next}</h3>
        {publish.blocks.length > 0 ? (
          <div className="pl-verdict pl-verdict--block">Publication impossible : {publish.blocks[0]!.message} <span className="rh-assign__meta">({publish.blocks[0]!.source}{publish.blocks[0]!.article ? ` · ${publish.blocks[0]!.article}` : ''})</span></div>
        ) : publish.diff.length === 0 && publish.version_next > 1 ? (
          <p className="lc-hint">Aucune modification depuis la v{publish.version_next - 1} — rien à publier.</p>
        ) : (
          <>
            <div className="pl-pubsum">
              <span><strong>{publish.diff.length}</strong> modification{publish.diff.length > 1 ? 's' : ''}</span>
              <span><strong>{publish.persons.length}</strong> salarié{publish.persons.length > 1 ? 's' : ''} concerné{publish.persons.length > 1 ? 's' : ''}</span>
              {publish.unassigned_changes > 0 ? <span><strong>{publish.unassigned_changes}</strong> sur des non assignés</span> : null}
            </div>
            <p className="lc-hint">Seuls les salariés réellement modifiés seront notifiés dans Gestion.</p>
            <div className="pl-difflist">
              {publish.persons.map((p) => (
                <div key={p.person_id} className="pl-diffperson">
                  <strong>{humanName(p.display_name)}</strong>
                  <span className="rh-assign__meta">{p.changes} changement{p.changes > 1 ? 's' : ''}{p.has_account ? '' : ' · pas de compte — non notifiable'}</span>
                  {publish.diff.filter((x) => x.person_id === p.person_id).map((x) => <DiffLine key={x.shift_id + x.kind} x={x} fmtSnap={fmtSnap} />)}
                </div>
              ))}
              {publish.persons.length === 0 ? <p className="lc-hint">Première publication : {publish.diff.length} shift{publish.diff.length > 1 ? 's' : ''}.</p> : null}
            </div>
          </>
        )}
        <div className="org-editor__foot">
          <button className="lc-btn lc-btn--ghost" onClick={onClose}>Annuler</button>
          <button className="lc-btn lc-btn--primary" disabled={publish.blocks.length > 0 || (publish.diff.length === 0 && publish.version_next > 1)} onClick={onPublish}>Publier la v{publish.version_next}</button>
        </div>
      </div>
    </Modal>
  )
}
function DiffLine({ x, fmtSnap }: { x: DiffEntry; fmtSnap: (s: SnapRow | null) => string }) {
  return (
    <span className={`pl-diffline pl-diffline--${x.kind.toLowerCase()}`}>
      <b>{x.kind === 'ADDED' ? '+' : x.kind === 'REMOVED' ? '−' : '~'}</b>
      {x.kind === 'ADDED' ? fmtSnap(x.after) : x.kind === 'REMOVED' ? fmtSnap(x.before) : <>{fmtSnap(x.before)} → {fmtSnap(x.after)}</>}
    </span>
  )
}

/* ═══════════════ Versions : brouillon / dernière publication / comparer ═══════════════ */
interface PlanningHistory { versions: { version: number; published_at: string; published_by: string; changes: number; affected: number; diff: DiffEntry[] }[] }
function VersionsModal({ week, diff, nameOf, fmtSnap, onClose }: { week: string; diff: DiffData | null; nameOf: (p: string | null) => string; fmtSnap: (s: SnapRow | null) => string; onClose: () => void }) {
  const [data, setData] = useState<PlanningHistory | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [open, setOpen] = useState<number | 'compare' | null>(null)
  // apiGet RÉSOUT les erreurs HTTP ({ error }) : sans mémoriser l'échec, « Chargement… » resterait affiché indéfiniment.
  useEffect(() => {
    setFailed(false); setData(null)
    apiGet<PlanningHistory & { error?: string; message?: string }>(`/api/v1/hr/planning/history?week=${week}`)
      .then((r) => { if ('versions' in r && Array.isArray(r.versions)) setData(r); else setFailed(true) })
      .catch(() => setFailed(true))
  }, [week, attempt])
  const last = diff?.last_publication ?? null
  return (
    <Modal open onClose={onClose} ariaLabel="Versions du planning" width={640} owning>
      <div className="org-editor">
        <h3>Versions — semaine {isoWeekNumber(week)}</h3>
        <div className="pl-versions__state">
          <div className="pl-versions__card">
            <span className="pl-versions__k">Brouillon actuel</span>
            <strong>{diff ? `${diff.pending_changes} modification${diff.pending_changes > 1 ? 's' : ''} non publiée${diff.pending_changes > 1 ? 's' : ''}` : '…'}</strong>
          </div>
          <div className="pl-versions__card">
            <span className="pl-versions__k">Dernière publication</span>
            <strong>{last ? `v${last.version} · ${new Date(last.published_at).toLocaleString('fr-FR', { timeZone: PARIS, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : 'Jamais publié'}</strong>
            {last ? <span className="rh-assign__meta">par {last.published_by}</span> : null}
          </div>
        </div>
        {diff && diff.pending_changes > 0 ? (
          <button className="lc-btn" onClick={() => setOpen(open === 'compare' ? null : 'compare')} aria-expanded={open === 'compare'}><Eye size={14} /> Comparer avec la dernière publication</button>
        ) : null}
        {open === 'compare' && diff ? (
          <div className="pl-difflist">
            {[...diff.persons, ...(diff.unassigned_changes ? [{ person_id: '', display_name: 'Non assignés', has_account: false, changes: diff.unassigned_changes }] : [])].map((p) => (
              <div key={p.person_id || 'una'} className="pl-diffperson">
                <strong>{p.person_id ? humanName(p.display_name) : 'Non assignés'}</strong>
                {diff.diff.filter((x) => (x.person_id ?? '') === p.person_id).map((x) => <DiffLine key={x.shift_id + x.kind} x={x} fmtSnap={fmtSnap} />)}
              </div>
            ))}
          </div>
        ) : null}
        <h4 className="pl-versions__h4">Historique publié</h4>
        {failed ? <p className="lc-hint">Historique indisponible. <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setAttempt((n) => n + 1)}>Réessayer</button></p>
          : !data ? <p className="lc-hint">Chargement…</p> : data.versions.length === 0 ? <p className="lc-hint">Jamais publié. La première publication créera la v1.</p> : data.versions.map((v) => (
          <div key={v.version} className="pl-version">
            <button className="pl-version__head" onClick={() => setOpen(open === v.version ? null : v.version)} aria-expanded={open === v.version}>
              <strong>v{v.version}</strong>
              <span className="rh-assign__meta">{new Date(v.published_at).toLocaleString('fr-FR', { timeZone: PARIS })} · {v.published_by} · {v.changes} modif. · {v.affected} salarié{v.affected > 1 ? 's' : ''}</span>
            </button>
            {open === v.version ? (
              <div className="pl-difflist">
                {v.diff.length === 0 ? <p className="lc-hint">Publication initiale.</p> : v.diff.map((x) => <span key={x.shift_id + x.kind} className="pl-diffline"><strong>{nameOf(x.person_id)}</strong> · <DiffLine x={x} fmtSnap={fmtSnap} /></span>)}
              </div>
            ) : null}
          </div>
        ))}
        <div className="org-editor__foot"><button className="lc-btn" onClick={onClose}>Fermer</button></div>
      </div>
    </Modal>
  )
}

/* ═══════════════ Modèles ═══════════════ */
function TemplatesModal({ week, templates, onClose, onChanged }: { week: string; templates: { id: string; name: string; shifts: number }[]; onClose: () => void; onChanged: () => void }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [preview, setPreview] = useState<{ id: string; created: number; skipped: { person: string; reason: string }[] } | null>(null)
  const [onConflict, setOnConflict] = useState<'SKIP' | 'UNASSIGN' | 'REPLACE'>('SKIP')
  const saveTemplate = async () => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/planning/templates', { week, name: name.trim() })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `Modèle « ${name.trim()} » enregistré.`); setName(''); onChanged()
  }
  const dryRun = async (id: string) => {
    const r = await adminMutate<{ error?: string; message?: string; created?: number; skipped?: { person: string; reason: string }[] }>('POST', '/api/v1/hr/planning/templates/apply', { week, template_id: id, dry_run: true, on_conflict: onConflict })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    setPreview({ id, created: r.data.created ?? 0, skipped: r.data.skipped ?? [] })
  }
  const apply = async () => {
    if (!preview) return
    const r = await adminMutate<{ error?: string; message?: string; created?: number }>('POST', '/api/v1/hr/planning/templates/apply', { week, template_id: preview.id, dry_run: false, on_conflict: onConflict })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `${r.data.created} shift(s) créé(s) depuis le modèle.`); onChanged(); onClose()
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Modèles de planning" width={560} owning>
      <div className="org-editor">
        <h3>Modèles de semaine</h3>
        <Field label="En cas de conflit avec un shift existant">
          <SegmentedControl value={onConflict} onChange={setOnConflict} size="sm" options={[{ value: 'SKIP', label: 'Conserver' }, { value: 'UNASSIGN', label: 'Non assigné' }, { value: 'REPLACE', label: 'Remplacer' }]} />
        </Field>
        {templates.length === 0 ? <p className="lc-hint">Aucun modèle. Enregistrez la semaine courante comme « Jeudi standard », « Samedi standard »…</p> : templates.map((t) => (
          <div className="rh-assign" key={t.id}>
            <span className="lc-stack"><strong>{t.name}</strong><span className="rh-assign__meta">{t.shifts} shifts</span></span>
            <button className="lc-btn lc-btn--ghost" onClick={() => void dryRun(t.id)}>Appliquer…</button>
          </div>
        ))}
        {preview ? (
          <div className="pl-verdict pl-verdict--info lc-stack" style={{ alignItems: 'stretch' }}>
            <span><strong>Aperçu :</strong> {preview.created} shift(s) créé(s){preview.skipped.length > 0 ? ` · ${preview.skipped.length} ignoré(s)` : ''}.</span>
            {preview.skipped.slice(0, 5).map((sk, i) => <span key={i} className="rh-assign__meta">{sk.person} — {sk.reason}</span>)}
            <div><button className="lc-btn lc-btn--primary" onClick={() => void apply()}>Confirmer</button></div>
          </div>
        ) : null}
        <div className="pl-editor__row" style={{ alignItems: 'flex-end' }}>
          <Field label="Enregistrer la semaine courante comme modèle"><TextInput value={name} onChange={setName} placeholder="Samedi standard…" /></Field>
          <button className="lc-btn" disabled={!name.trim()} onClick={() => void saveTemplate()}>Enregistrer</button>
        </div>
        <div className="org-editor__foot"><button className="lc-btn" onClick={onClose}>Fermer</button></div>
      </div>
    </Modal>
  )
}

/* ═══════════════ Effacer la semaine ═══════════════ */
function ClearWeekModal({ week, teams, onClose, onDone }: { week: string; teams: string[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [scope, setScope] = useState('')
  const run = async () => {
    const body: Record<string, unknown> = { week, include_unassigned: scope === '' }
    if (scope) body['team'] = scope
    const r = await adminMutate<{ error?: string; message?: string; deleted?: number }>('POST', '/api/v1/hr/planning/clear-week', body)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `${r.data.deleted ?? 0} shift(s) supprimé(s).`); onDone()
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Effacer la semaine" width={460} owning>
      <div className="org-editor">
        <h3>Effacer les shifts de la semaine</h3>
        <p className="lc-hint">Les absences ne sont pas des shifts : elles ne sont pas touchées.</p>
        <Field label="Portée"><Combobox value={scope || null} items={[{ value: '', label: 'Tout l’effectif' }, ...teams.map((t) => ({ value: t, label: `Équipe ${t}` }))]} onChange={(v) => setScope(v ?? '')} placeholder="Tout l’effectif" /></Field>
        <div className="org-editor__foot">
          <button className="lc-btn lc-btn--ghost" onClick={onClose}>Annuler</button>
          <button className="lc-btn lc-btn--danger" onClick={() => void run()}>Effacer</button>
        </div>
      </div>
    </Modal>
  )
}
